import { scenarioLivingExpensesOr } from '../../../../shared/src/calculation/support/livingExpenses.ts';
import { agedCareEntrantResidencyOverrides, isIllnessSeparatedCouple } from '../../../../shared/src/calculation/householdStatus.ts';
import { movePiece } from './facilityMoveWords.ts';
import { villagePiece, refundByLabel } from './villageExitWords.ts';
import { homeRuleWords, estateWorthConsidering, horizonSetAsideSummaryLine, situationBridgeWords } from './soaReviewWords.ts';
import { stayPutEstate } from './stayPutEstate.ts';
import { describeFundingKeys, fundingPhrase } from './fundingLabels.ts';
import { phaseFromLabel, recapPhaseLine, roleNames } from './carePhases.ts';
import { pensionCallout } from '../advice/agePensionClaim.ts';
import { householdCoveredByNoWorseOffPrinciple } from '../../../../shared/src/calculation/residentialFeeArrangements.ts';
import { getCurrentAdviceAttribution } from '../professionalPortal/adviceAttribution.ts';

/** The funding source name used for the deposit, so the scan and the sentence cannot disagree. */
const RAD_SOURCE = 'the refundable accommodation deposit';
import * as calculationApi from '../../services/calculationApi.ts';
import { calculateAge, formatCurrency } from '../../utils/index.ts';
import { capitalReturnedWords } from '../../components/strategy/annuityWords.ts';
import { engineStrategyHorizonWarning, personHorizons, type HorizonSetAside } from './strategyHorizonFlags.ts';
import { buildStrategyRisks, riskKindsFromAgedCareStrategy, type StrategyRisks } from '../../strategy/strategyRisks.ts';
import { buildAssumptionSections } from '../advice/assumptionSections.ts';
import { SOA_THEME_CSS, situationFrom, renderSituationHtml, renderProjectionCharts, renderAssumptionsAppendixHtml } from '../advice/soaKit.ts';
import { isBorrowingRow, isRadDrawdownRow, planRunwayCopy, summariseFunding, type FundingProjectionYear } from '../../components/insights/fundingSummary.tsx';
import { anyRateAboveDefault, listAssetRateOverrides } from '../../../../shared/src/calculation/assetRateOverrides.ts';
import { ACAT_LEVEL_DATA } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { AGED_CARE_SCHEDULE } from '../../../../shared/src/calculation/constants/agedCareSchedule.ts';
import { PENSION_RATES } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { assetAssumptionFiguresFor } from '../../../../shared/src/calculation/assetAssumptionRows.ts';
import { ASSET_TYPES } from '../../../../shared/src/calculation/constants/assetTypes.ts';
import {
    buildCgtAssumptionDisclosure,
    findRealisedCgtDataGaps,
    type RealisedAssetRef,
} from '../../../../shared/src/calculation/cgtDataQuality.ts';
import { computeRadFundingTotals } from '../../../../shared/src/calculation/support/entityFundingUtils.ts';
import {
    computeFutureEventAdjustedLegacy,
    estimateRadPaidForStrategy,
    pickLowerRadNearBestStrategy,
    formatRadStrategyDisplayName
} from './futureLifeEventUtils.tsx';
import type { EvaluatedStrategy } from './futureLifeEventUtils.tsx';
import type { RadFundingBag } from './getRadFundingTooltipData.tsx';
import {
    SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD,
    LOWER_RAD_NEAR_BEST_LEGACY_BADGE
} from '../../constants/insightsConfig.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAssetMap, FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

// DraftFactFind is structurally compatible with FactFind (formDraft.ts); the shared
// calc-engine fns below defensively coerce internally regardless (factFindTypes.ts header).
const asFactFind = (factFind: DraftFactFind): FactFind => factFind as unknown as FactFind;

/**
 * This module builds the printable Digital SOA (Statement of Advice) attachment: it stitches together
 * the fact-find, the suggestion-engine's chosen scenario, and the wider `analysis.strategies` list into
 * one big HTML report. Almost everything here reads a calc-engine result blob (scenario/snapshot/
 * projection rows returned by calculationApi.ts's `Promise<any>` calls) rather than a persisted
 * fact-find shape, so — per shared/src/calculation/factFindTypes.ts's own doc comment and
 * calculationApi.ts's header — these stay intentionally loose (`Record<string, unknown>` / optional
 * fields with an index signature) instead of one giant hand-modelled interface. `factFind` parameters
 * ARE fact-find shaped and use `DraftFactFind` throughout.
 */

/** One row of a projection year's `drawdownBreakdown` (which liquid asset funded that year's deficit). */
interface SoaBreakdownItem {
    label?: string;
    value?: number | string;
    [key: string]: unknown;
}

/** One asset row inside a projection year's `assets` map (loose — mirrors DraftAsset's key fields). */
interface SoaAssetRow {
    value?: number | string;
    balance?: number | string;
    type?: string;
    residualValue?: number | string;
    name?: string;
    description?: string;
    [key: string]: unknown;
}

interface SoaFyProjectionRow {
    socialSecurity?: number;
    annualCareCost?: number;
    [key: string]: unknown;
}

interface SoaFyFullYearView {
    fyLabel?: string;
    daysPre?: number;
    daysPost?: number;
    netWorthOpening?: number;
    blended?: {
        socialSecurity?: number;
        annualCareCost?: number;
        cashFlow?: number;
        tax?: number;
        livingExpenses?: number;
        [key: string]: unknown;
    };
    baselineProjectionRow?: SoaFyProjectionRow;
    strategyProjectionRow?: SoaFyProjectionRow;
    [key: string]: unknown;
}

/** One year of a scenario's `projections` array (calc-engine REST response — see calculationApi.ts). */
interface SoaProjectionYear {
    year?: number;
    cashFlow?: number;
    cashflow?: number;
    netWorth?: number;
    netWorthBreakdown?: SoaBreakdownItem[];
    socialSecurity?: number;
    pension?: number;
    socialSecurityBreakdown?: SoaBreakdownItem[];
    annualCareCost?: number;
    agedCareBreakdown?: SoaBreakdownItem[];
    cashFlowBreakdown?: SoaBreakdownItem[];
    /** Human-readable HTML breakdown of the year's calculations (see server `ProjectionYearResult.breakdown`). */
    breakdown?: string;
    cashflowRoutingLine?: SoaBreakdownItem;
    tax?: number;
    fyLabel?: string;
    isPartialFy?: boolean;
    periodStart?: string;
    periodEnd?: string;
    fyFullYearView?: SoaFyFullYearView;
    home?: { value?: number | string; description?: string; [key: string]: unknown };
    assets?: Record<string, SoaAssetRow[] | undefined>;
    liabilities?: Record<string, { balance?: number | string; [key: string]: unknown }[] | undefined>;
    drawdownBreakdown?: SoaBreakdownItem[];
    [key: string]: unknown;
}

/** A suggestion-engine scenario ("strategy") — see calculationApi.ts's evaluate…/generateAllStrategies family. */
interface SoaScenario {
    name?: string;
    /** The annuity terms the engine built this strategy on, where it buys one. */
    annuityTerms?: Record<string, unknown> | null;
    snapshot?: {
        legacyValue?: number;
        netCashflow?: number;
        pension?: number;
        totalAgedCareCosts?: number;
        [key: string]: unknown;
    };
    projections?: SoaProjectionYear[];
    /**
     * The same plan re-run at AgedCareWise's default rates — present only where the client set a rate
     * of their own (assetRateOverrides.ts). Struck by the engine that produced `projections`, on the
     * same applied balance sheet, so the two differ in nothing but the rates.
     */
    atDefaultRatesProjections?: SoaProjectionYear[] | null;
    /** Which residential fee arrangements the candidate was priced on ('2014' = no-worse-off means-tested care fee). */
    feeArrangements?: '2014' | '2025';
    /** The same plan with the resident opted in to the 1 November 2025 rules — null unless they are on the 2014 arrangements. */
    optInProjections?: SoaProjectionYear[] | null;
    radFunding?: RadFundingBag;
    radPaidNet?: number;
    strategyOptions?: Record<string, unknown>;
    scenarioConfig?: Record<string, unknown>;
    strategySpecificAgedCareCosts?: Record<string, unknown>;
    context?: string;
    description?: string;
    outputs?: {
        legacyEstimate?: number;
        cashFlow?: number;
        agePension?: number;
        [key: string]: unknown;
    };
    inputs?: {
        radFunding?: RadFundingBag;
        /** The second room, where both of a couple enter care (a manual plan's saved inputs). */
        secondRoom?: { roomPrice: number; careStartDate?: string; radPaid: number } | null;
        [key: string]: unknown;
    };
    cashFlow?: number;
    cashflow?: number;
    agePension?: number;
    estimatedAgePension?: number;
    [key: string]: unknown;
}

interface SoaRecommendedOptimalStrategyRow {
    strategy: SoaScenario;
    label: string;
    [key: string]: unknown;
}

interface SoaAnalysis {
    allSimulatedStrategies?: SoaScenario[];
    strategies?: SoaScenario[];
    recommendedOptimalStrategies?: SoaRecommendedOptimalStrategyRow[];
    totalScenarios?: number;
    [key: string]: unknown;
}

// Served as a same-origin <script src> in the popup HTML below rather than inlined, so the popup
// (which inherits this app's CSP — window.open('') + document.write and the blob: fallback both
// count as CSP-inheriting navigations) doesn't need 'unsafe-inline'/a content hash in script-src.
const soaBookletScriptUrl = new URL('./soaBookletScript.ts', import.meta.url).href;
import {
    buildDigitalSoaStructureAppendix,
    buildDigitalSoaStructureAppendixHtml,
} from '../../utils/digitalSoaStructureAppendix.ts';
import { ratesReviewStatus } from '../../../../shared/src/calculation/ratesReview.ts';
import { describeProjectionPeriod, projectionPeriodConventionSentence } from '../../../../shared/src/calculation/projectionPeriodDescription.ts';
import { parseIsoDate, formatLongDate } from '../../../../shared/src/calculation/support/dateHelpers.ts';
import { stripFundingOrderSuffix } from './futureLifeEventUtils.tsx';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import { stockPhoto } from '../../assets/stock/index.ts';
const GOAL_LABELS: Record<string, string> = {
    home_care: 'Access home care support',
    aged_care: 'Move into residential aged care',
    downsize_home: 'Downsize the family home',
    retirement_village: 'Move to a retirement village',
    granny_flat: 'Explore granny flat arrangements',
    meet_costs: 'Meet care costs',
    maximise_support: 'Maximise government support',
    preserve_assets: 'Preserve assets for inheritance',
    funding_guidance: 'Guidance on funding options'
};
const LIVING_ARRANGEMENT_GOAL_KEYS = new Set(['home_care', 'aged_care', 'retirement_village', 'granny_flat']);

/* The funding order only — see futureLifeEventUtils. A broader strip put two different strategies
   under one name, which in a Statement of Advice is worse than clutter. */
const stripFundingSuffix = (name = '') => stripFundingOrderSuffix(name);

const ASSET_IMPLICATION_TEMPLATES: Record<string, string> = {
    bankAccounts:
        'Using cash from bank accounts immediately reduces your liquid emergency buffer and may reduce interest income. Keep enough short-term liquidity for ongoing fees and unexpected costs.',
    termDeposits:
        'Breaking a term deposit early can trigger break costs, reduced interest, or repricing by the provider. Confirm break conditions with the institution before implementation.',
    shares:
        'Selling shares can crystallise capital gains or losses and changes your future dividend/growth profile. Transaction costs and timing can materially affect net proceeds.',
    accountBasedPensions:
        'Drawing from an account-based pension reduces the pension balance available for future income and may alter minimum-drawdown and Centrelink outcomes over time.',
    superannuation:
        'Commuting or withdrawing super changes retirement-phase balances and future income capacity. Tax treatment depends on age, component mix, and payment type.',
    investmentProperty:
        'Selling investment property can trigger CGT, selling costs, and potential changes to rental income assumptions. Final tax outcomes depend on ownership and cost-base records.',
    annuities:
        'Commuting or exiting annuity-style products may trigger surrender adjustments or provider-imposed exit terms. Confirm product conditions before proceeding.',
    other:
        'Selling non-standard assets can involve valuation, tax, and transaction complexity. Check legal and tax consequences before actioning the strategy.'
};

function toNumber(value: unknown): number {
    const parsed = parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : 0;
}

function sanitizeText(value: unknown): string {
    return String(value || '')
        .replace(/Ã¢â‚¬â€œ|â€“/g, '-')
        .replace(/Ã¢â‚¬â€”|â€”/g, '-')
        .replace(/Ã¢â‚¬â„¢|â€™/g, "'")
        .replace(/Ã¢â‚¬Å“|Ã¢â‚¬Â|â€œ|â€/g, '"')
        .replace(/Ã‚/g, '')
        .trim();
}

/**
 * Escape text that came from a FIRM for a template that is assembled as a string.
 *
 * The attribution line is built from names the firm typed — licensee, practice, adviser — and this
 * document is concatenated HTML rather than rendered by React, so nothing else stands between those
 * values and the page. sanitizeText next door repairs mojibake; it does not make markup inert.
 */
function escapeHtml(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** A field key as a label: `home_care_for` and `annuityAppetite` both read "Home Care For" / "Annuity Appetite". */
function prettify(text: unknown): string {
    const words = String(text || '').replace(/([A-Z])/g, ' $1').split(/[_\s]+/).filter(Boolean);
    return sanitizeText(words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
}

function formatDateOnly(dateValue: string | number | Date): string {
    return new Date(dateValue).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: '2-digit' });
}

/**
 * A stored value as the appendix prints it. Booleans are Yes/No; a stored word or code is shown in
 * sentence case with its underscores as spaces ("both" → "Both", "own_home" → "Own home"), so every
 * row reads the same way (owner, 15 Oct 2026: "Yes" beside "yes" in the one section).
 */
export function toDisplay(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    const text = String(value);
    if (/^[a-z][a-z0-9_ ]*$/i.test(text) && !/^\d/.test(text)) {
        const spaced = text.replace(/_/g, ' ');
        return sanitizeText(spaced.charAt(0).toUpperCase() + spaced.slice(1));
    }
    return sanitizeText(text);
}

interface FlattenedEntry {
    key: string;
    value: string;
}

function flattenObject(value: unknown, prefix = ''): FlattenedEntry[] {
    if (value === null || value === undefined) return [];
    if (Array.isArray(value)) return value.flatMap((item, idx) => flattenObject(item, `${prefix}[${idx}]`));
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).flatMap(([key, nested]) => flattenObject(nested, prefix ? `${prefix}.${key}` : key));
    }
    return [{ key: prefix, value: String(value) }];
}

function parseMoneyish(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    return parseFloat(String(value ?? '').replace(/[$,]/g, '')) || 0;
}

function classifyPensionLevel(annualPension: unknown, annualMaximum: unknown): string {
    const amount = Number(annualPension) || 0;
    const max = Number(annualMaximum) || 0;
    if (amount <= 0) return 'not currently receiving Age Pension';
    if (max > 0 && amount >= max * 0.95) return 'receiving close to the full Age Pension';
    return 'receiving a part Age Pension';
}

const HEALTH_STATUS_LABELS: Record<string, string> = {
    good: 'good overall health',
    fair: 'fair health (some support needs)',
    poor: 'poorer health (significant support needs)'
};

function healthStatusLabel(status: unknown): string {
    if (!status) return 'health status not specified';
    return HEALTH_STATUS_LABELS[String(status).toLowerCase()] || sanitizeText(String(status));
}

function ynUnsureLabel(v: unknown): string {
    if (v === true || v === 'yes') return 'Yes';
    if (v === false || v === 'no') return 'No';
    if (v === 'unsure') return 'Unsure';
    return toDisplay(v);
}

function ynRecordLabel(v: unknown): string {
    if (v === undefined || v === null || v === '') return 'Not recorded in fact-find';
    return ynUnsureLabel(v);
}

interface SoaYearBreakdownEntry {
    year: number;
    cashflow: number;
    isDeficit: boolean;
    deficitAmount: number;
    bankBalance: number;
    abpBalance: number;
    sharesBalance: number;
    fundingSources: (string | undefined)[];
    primaryFundingSource: string | null | undefined;
}

/**
 * Mirrors AgedCareInsightPanel.analyzeMultiYearCashflow for SOA export (no React).
 */
function analyzeMultiYearCashflowForSOA(projectionsData: SoaProjectionYear[] | null | undefined, formatMoney: (value: number) => string) {
    if (!projectionsData || projectionsData.length === 0) return null;

    const year1Cashflow = projectionsData[0]?.cashFlow ?? projectionsData[0]?.cashflow ?? 0;
    const isYear1Surplus = year1Cashflow > 0;

    const yearBreakdown: SoaYearBreakdownEntry[] = [];
    let firstDeficitYear: number | null = null;
    let firstDeficitAmount = 0;
    let hasDeficitYears = false;

    for (let i = 0; i < projectionsData.length; i++) {
        const projection = projectionsData[i];
        const year = projection?.year ?? i + 1;
        const cashflow = projection?.cashFlow ?? projection?.cashflow ?? 0;
        const isDeficit = cashflow < 0;
        const deficitAmount = isDeficit ? Math.abs(cashflow) : 0;

        const bankBalance = (projection?.assets?.bankAccounts || []).reduce((sum, acc) => sum + (parseFloat(String(acc.value)) || 0), 0);
        const abpBalance = (projection?.assets?.accountBasedPensions || []).reduce((sum, abp) => sum + (parseFloat(String(abp.value)) || 0), 0);
        const sharesBalance = (projection?.assets?.shares || []).reduce((sum, share) => sum + (parseFloat(String(share.value)) || 0), 0);

        const drawdownBreakdown = projection?.drawdownBreakdown || [];
        const fundingSources = drawdownBreakdown
            .filter((item) => parseMoneyish(item.value) > 0)
            .map((item) => item.label);

        let primaryFundingSource: string | null | undefined = null;
        if (isDeficit && fundingSources.length > 0) {
            primaryFundingSource = fundingSources[0];
        } else if (isDeficit && fundingSources.length === 0) {
            if (bankBalance > 0) primaryFundingSource = 'Bank Account';
            else if (abpBalance > 0) primaryFundingSource = 'Account-Based Pensions';
            else if (sharesBalance > 0) primaryFundingSource = 'Shares';
        }

        yearBreakdown.push({
            year,
            cashflow,
            isDeficit,
            deficitAmount,
            bankBalance,
            abpBalance,
            sharesBalance,
            fundingSources,
            primaryFundingSource
        });

        if (isDeficit && firstDeficitYear === null) {
            firstDeficitYear = year;
            firstDeficitAmount = deficitAmount;
            hasDeficitYears = true;
        }
    }

    const buildDetailedNarrative = (): string | null => {
        if (!hasDeficitYears) return null;
        const narrative: string[] = [];
        let currentFundingSource: string | null | undefined = null;
        const transitionPoints: { year: number; from: string | null | undefined; to: string | null | undefined }[] = [];

        for (let i = 0; i < yearBreakdown.length; i++) {
            const breakdown = yearBreakdown[i];
            if (breakdown.isDeficit) {
                const funding = breakdown.primaryFundingSource;
                if (currentFundingSource !== funding) {
                    if (currentFundingSource !== null) {
                        transitionPoints.push({
                            year: breakdown.year,
                            from: currentFundingSource,
                            to: funding
                        });
                    }
                    currentFundingSource = funding;
                }
            }
        }

        if (transitionPoints.length > 0) {
            narrative.push(`Year ${firstDeficitYear} begins with a deficit of ${formatMoney(firstDeficitAmount)}.`);
            transitionPoints.forEach((transition, idx) => {
                if (idx === 0 && transition.year === firstDeficitYear) {
                    narrative.push(`The deficit is initially funded from your ${transition.to}.`);
                } else {
                    narrative.push(
                        `From year ${transition.year}, funding shifts from ${transition.from} to ${transition.to}.`
                    );
                }
            });
        } else if (firstDeficitYear != null) {
            narrative.push(
                `From year ${firstDeficitYear}, the model shows an annual shortfall of about ${formatMoney(firstDeficitAmount)}, met from liquid assets in the order shown in the projection funding breakdown.`
            );
        }

        return narrative.length > 0 ? narrative.join(' ') : null;
    };

    return {
        year1Cashflow,
        isYear1Surplus,
        firstDeficitYear,
        firstDeficitAmount,
        hasDeficitYears,
        yearBreakdown,
        detailedNarrative: buildDetailedNarrative()
    };
}

/**
 * Mirrors AgedCareInsightPanel.getDeficitFundingExplanation for SOA export.
 */
function getDeficitFundingExplanationForSOA(cashFlowValue: number, projectionsToUse: SoaProjectionYear[] | null | undefined, formatMoney: (value: number) => string): string | null {
    if (cashFlowValue >= 0 || !projectionsToUse?.length) return null;
    const targetProjection = projectionsToUse[0];
    const drawdownBreakdown = targetProjection?.drawdownBreakdown || [];
    const deficitAmount = Math.abs(cashFlowValue);

    if (drawdownBreakdown.length === 0) {
        return `The model funds this shortfall (${formatMoney(deficitAmount)} per year) from liquid assets in order: bank accounts first, then account-based pensions, then listed shares.`;
    }

    const firstAsset = drawdownBreakdown[0]!;
    const firstLabel = (firstAsset.label || '').toLowerCase();

    if (firstLabel.includes('bank') || firstLabel.includes('acc')) {
        let yearBankDepletes: number | null = null;
        let nextAsset: string | null = null;

        for (let i = 0; i < projectionsToUse.length; i++) {
            const yearData = projectionsToUse[i]!;
            let totalBank = 0;
            if (yearData?.assets?.bankAccounts?.length) {
                yearData.assets.bankAccounts.forEach((acc) => {
                    totalBank += parseFloat(String(acc.value)) || 0;
                });
            }
            if (totalBank < 1000 && yearBankDepletes === null) {
                yearBankDepletes = yearData.year ?? i + 1;
                const breakdown = yearData.drawdownBreakdown || [];
                for (const item of breakdown) {
                    const normalized = (item.label || '').toLowerCase();
                    const amount = parseMoneyish(item.value);
                    if (amount > 0) {
                        if (normalized.includes('account-based pension') || normalized.includes('abp')) {
                            nextAsset = item.label || 'account-based pensions';
                            break;
                        }
                        if (normalized.includes('share')) {
                            nextAsset = item.label || 'shares';
                            break;
                        }
                        if (isRadDrawdownRow(item)) {
                            nextAsset = RAD_SOURCE;
                            break;
                        }
                        if (isBorrowingRow(item)) {
                            nextAsset = 'borrowing against the home';
                            break;
                        }
                    }
                }
                break;
            }
        }

        if (yearBankDepletes != null) {
            if (!nextAsset) {
                const startIndex = Math.max(0, yearBankDepletes - 1);
                for (let i = startIndex; i < projectionsToUse.length; i++) {
                    const yearData = projectionsToUse[i];
                    const breakdown = yearData?.drawdownBreakdown || [];
                    for (const item of breakdown) {
                        const normalized = (item.label || '').toLowerCase();
                        const amount = parseMoneyish(item.value);
                        if (amount > 0 && !normalized.includes('bank') && !normalized.includes('acc')) {
                            if (normalized.includes('account-based pension') || normalized.includes('abp')) {
                                nextAsset = item.label || 'account-based pensions';
                                break;
                            }
                            if (normalized.includes('share')) {
                                nextAsset = item.label || 'shares';
                                break;
                            }
                            if (isRadDrawdownRow(item)) {
                                nextAsset = RAD_SOURCE;
                                break;
                            }
                            if (isBorrowingRow(item)) {
                                nextAsset = 'borrowing against the home';
                                break;
                            }
                            if (!nextAsset) nextAsset = item.label || null;
                        }
                    }
                    if (nextAsset) break;
                }
            }

            if (nextAsset) {
                if (nextAsset === RAD_SOURCE) {
                    return `The model uses your bank account first to cover the ${formatMoney(deficitAmount)} annual shortfall. Bank cash is projected to fall below a nominal buffer by year ${yearBankDepletes}; from that point the care fees are drawn from your refundable accommodation deposit. Each dollar drawn reduces the amount refunded when care ends and increases the daily accommodation payment, which is charged on the unpaid part of the room at the maximum permissible interest rate. Only fees owed to the provider are drawn this way; living expenses are not.`;
                }
                if (isBorrowingRow({ label: nextAsset })) {
                    return `The model uses your bank account first to cover the ${formatMoney(deficitAmount)} annual shortfall. Bank cash is projected to fall below a nominal buffer by year ${yearBankDepletes}; after that, the shortfall is met by borrowing against the home through a reverse mortgage at about ${REVERSE_MORTGAGE_DEFAULT_RATE}% p.a. Nothing is repaid, so interest is added to the balance each year and it compounds until the home is sold.`;
                }
                return `The model uses your bank account first to cover the ${formatMoney(deficitAmount)} annual shortfall. Bank cash is projected to fall below a nominal buffer by year ${yearBankDepletes}; from that point the shortfall is met from ${nextAsset}.`;
            }

            let hasOtherAssets = false;
            const startIdx = Math.max(0, (yearBankDepletes || 1) - 1);
            for (let i = startIdx; i < projectionsToUse.length; i++) {
                const yearData = projectionsToUse[i];
                if (yearData?.assets?.accountBasedPensions?.length) {
                    const totalABP = yearData.assets.accountBasedPensions.reduce((s, a) => s + (parseFloat(String(a.value)) || 0), 0);
                    if (totalABP > 1000) {
                        hasOtherAssets = true;
                        break;
                    }
                }
                if (yearData?.assets?.shares?.length) {
                    const totalShares = yearData.assets.shares.reduce((s, sh) => s + (parseFloat(String(sh.value)) || 0), 0);
                    if (totalShares > 1000) {
                        hasOtherAssets = true;
                        break;
                    }
                }
                const breakdown = yearData?.drawdownBreakdown || [];
                for (const item of breakdown) {
                    const normalized = (item.label || '').toLowerCase();
                    const amount = parseMoneyish(item.value);
                    if (amount > 0 && !normalized.includes('bank') && !normalized.includes('acc')) {
                        hasOtherAssets = true;
                        break;
                    }
                }
                if (hasOtherAssets) break;
            }

            if (hasOtherAssets) {
                return `The model uses your bank account first for the ${formatMoney(deficitAmount)} shortfall. By year ${yearBankDepletes} bank balances are projected to be largely exhausted; ongoing deficits are then met from other liquid assets (typically account-based pensions, then shares) in the order modelled in the year-by-year drawdown detail.`;
            }
            return `The model uses your bank account for the ${formatMoney(deficitAmount)} shortfall; by year ${yearBankDepletes} bank cash is largely exhausted and the projection shows insufficient other liquidity to fully fund the deficit — revisit assumptions or funding options in AgedCareWise.`;
        }
        return `The model meets the ${formatMoney(deficitAmount)} annual shortfall from your bank account across the projection horizon.`;
    }

    if (firstLabel.includes('account-based pension') || firstLabel.includes('abp')) {
        return `The model meets the ${formatMoney(deficitAmount)} annual shortfall primarily from account-based pensions.`;
    }
    if (firstLabel.includes('share')) {
        return `The model meets the ${formatMoney(deficitAmount)} annual shortfall primarily from listed shares (drawdowns).`;
    }
    if (isRadDrawdownRow(drawdownBreakdown[0])) {
        return `The model meets the ${formatMoney(deficitAmount)} shortfall primarily by drawing care fees from the refundable accommodation deposit, which reduces the refund when care ends and increases the daily accommodation payment on the amount drawn.`;
    }
    if (isBorrowingRow(drawdownBreakdown[0])) {
        if (drawdownBreakdown.length === 1) {
            return `The model meets the ${formatMoney(deficitAmount)} shortfall by borrowing against the home through a reverse mortgage at about ${REVERSE_MORTGAGE_DEFAULT_RATE}% p.a.; nothing is repaid, so the balance compounds until the home is sold.`;
        }
        const otherAssets = drawdownBreakdown
            .filter((d) => !isBorrowingRow(d))
            .map((d) => d.label)
            .join(', then ');
        return `The model meets the ${formatMoney(deficitAmount)} shortfall from ${otherAssets} first; if those sources are exhausted, remaining gaps are funded by borrowing against the home through a reverse mortgage (about ${REVERSE_MORTGAGE_DEFAULT_RATE}% p.a., no repayments) as modelled.`;
    }

    const hasHeloc = drawdownBreakdown.some((d) => isBorrowingRow(d));
    if (hasHeloc) {
        const nonHelocAssets = drawdownBreakdown
            .filter((d) => !isBorrowingRow(d))
            .map((d) => d.label);
        let explanation = `Liquid assets are drawn in this order: ${nonHelocAssets.join(', then ')}`;
        explanation +=
            `. If those sources are insufficient, the model may borrow against the home through a reverse mortgage at about ${REVERSE_MORTGAGE_DEFAULT_RATE}% p.a.; nothing is repaid, so the balance compounds until the home is sold.`;
        return explanation;
    }
    return `The model allocates the ${formatMoney(deficitAmount)} shortfall across: ${drawdownBreakdown.map((d) => d.label).join(', then ')}.`;
}

function buildProjectionFootnote(
    cashflowAnalysis: ReturnType<typeof analyzeMultiYearCashflowForSOA>,
    annualCashflow: number,
    formatMoney: (value: number) => string,
): string {
    if (!cashflowAnalysis) {
        if (annualCashflow >= 0) {
            return `Annual cashflow is positive (${formatMoney(annualCashflow)}). Surpluses in the model are retained inside the balance sheet (higher bank or investment balances unless otherwise drawn for RAD or other cash needs in that year).`;
        }
        return `Cashflow is projected to be negative in the selected strategy; see the funding explanation and year-by-year projection details.`;
    }
    if (cashflowAnalysis.isYear1Surplus && !cashflowAnalysis.hasDeficitYears) {
        return `Projections show a surplus each year. Those surpluses accumulate in the assets modelled (unless consumed by one-off costs such as RAD funding in an earlier year).`;
    }
    if (cashflowAnalysis.isYear1Surplus && cashflowAnalysis.hasDeficitYears) {
        return `You start with a surplus of ${formatMoney(cashflowAnalysis.year1Cashflow)} in the first projection year (Comparison Yr1 when the app uses financial-year rows); the model later shows a shortfall from projection year ${cashflowAnalysis.firstDeficitYear} of about ${formatMoney(cashflowAnalysis.firstDeficitAmount)} per year unless assumptions change.`;
    }
    if (!cashflowAnalysis.isYear1Surplus && cashflowAnalysis.hasDeficitYears) {
        return `The modelled household cashflow is in deficit from the first projection year; funding is sourced as described above and in each year’s drawdown breakdown.`;
    }
    return '';
}

function acatStatusLabel(status: unknown): string {
    const map: Record<string, string> = { yes: 'Yes — assessment completed', no: 'No', in_progress: 'In progress' };
    return map[String(status)] || toDisplay(status);
}

type SoaAcatPerson = NonNullable<NonNullable<DraftFactFind['careNeeds']>['clientAcat']>;

export interface SoaFactRow {
    label: string;
    value: unknown;
}

function buildAcatFactRows(personData: SoaAcatPerson | null | undefined, personLabel: string): SoaFactRow[] {
    if (!personData) return [];
    const rows: SoaFactRow[] = [{ label: `${personLabel} — Aged care needs assessment completed?`, value: acatStatusLabel(personData.status) }];
    if (personData.status !== 'yes') return rows;

    rows.push({ label: `${personLabel} — Home Care Package in place, approved or on the waiting list at 12 Sep 2024`, value: personData.hcpBeforeSep2024 === 'unsure' ? 'Not sure — standard rules assumed (usually the higher fees)' : toDisplay(personData.hcpBeforeSep2024) });
    if (personData.hcpBeforeSep2024 === 'yes') {
        rows.push({ label: `${personLabel} — Residential fee arrangements`, value: '1 July 2014 (no worse off) — means-tested care fee unless opted in' });
        if (Number(personData.priorCareContributionsPaid) > 0) {
            rows.push({ label: `${personLabel} — Care contributions already paid`, value: formatCurrency(Number(personData.priorCareContributionsPaid)) });
        }
        rows.push({
            label: `${personLabel} — Previous HCP contribution category`,
            value:
                personData.hcpCategory === 'A'
                    ? 'No income-tested care fee was payable'
                    : personData.hcpCategory === 'B'
                      ? 'An income-tested care fee was payable'
                      : toDisplay(personData.hcpCategory)
        });
        if (personData.hcpCategory === 'B' && personData.previousITCF != null) {
            rows.push({ label: `${personLabel} — Previous income-tested care fee (daily)`, value: `$${personData.previousITCF}` });
        }
    }

    const levelKey = personData.homeCareLevel;
    const levelLabel = levelKey && ACAT_LEVEL_DATA[levelKey] ? ACAT_LEVEL_DATA[levelKey].label : toDisplay(levelKey);
    const transitioned = !!(levelKey && ACAT_LEVEL_DATA[levelKey]?.transitional);
    rows.push({ label: `${personLabel} — ${transitioned ? 'Home care funding level (transitioned package)' : 'Approved Support at Home classification'}`, value: levelLabel || 'Not specified' });

    const rec = personData.recommendation || {};
    const approved: string[] = [];
    if (rec.residential) approved.push('Approved for residential aged care');
    if (rec.respite) approved.push('Approved for residential respite care');
    rows.push({
        label: `${personLabel} — Care approvals selected`,
        value: approved.length ? approved.join('; ') : 'None selected'
    });

    return rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '' && row.value !== 'Not provided');
}

export interface SoaFactFindSection {
    title: string;
    rows: SoaFactRow[];
}

// Exported for the Support at Home Digital SOA — the structured-inputs appendix must be IDENTICAL
// across both documents, so there is exactly one walker of the fact-find.
export function buildFactFindSections(factFind: DraftFactFind = {} as DraftFactFind): SoaFactFindSection[] {
    const sections: SoaFactFindSection[] = [];
    const addSection = (title: string, rows: SoaFactRow[]) => {
        const filtered = rows.filter((row) => row.value !== undefined && row.value !== null && row.value !== '');
        if (filtered.length > 0) sections.push({ title, rows: filtered });
    };
    // willReduceExpensesAfterCare/reducedLivingExpensesAfterCare are legacy/planned fields not in
    // FactFindExpenses (shared/src/calculation/factFindTypes.ts) — same "no current writer" pattern
    // documented there for a few other fields; kept here defensively for any older saved profile.
    const expensesLegacy = (factFind.expenses || {}) as Record<string, unknown>;

    addSection('Client and Partner Details', [
        { label: 'Advice For', value: factFind.adviceFor },
        { label: 'Client Name', value: `${factFind.client?.firstName || ''} ${factFind.client?.lastName || ''}`.trim() },
        { label: 'Client Date of Birth', value: factFind.client?.dob },
        { label: 'Client Health Status', value: factFind.client?.healthStatus },
        { label: 'Client Private Health Insurance', value: factFind.client?.phi },
        { label: 'Partner Name', value: `${factFind.partner?.firstName || ''} ${factFind.partner?.lastName || ''}`.trim() },
        { label: 'Partner Date of Birth', value: factFind.partner?.dob },
        { label: 'Partner Health Status', value: factFind.partner?.healthStatus },
        { label: 'Partner Private Health Insurance', value: factFind.partner?.phi }
    ]);

    addSection('Application Completion Details', [
        { label: 'Completed By', value: factFind.completer || 'client' },
        { label: 'Completer First Name', value: factFind.completerFirstName },
        { label: 'Completer Last Name', value: factFind.completerLastName },
        { label: 'Completer Relationship', value: factFind.completerRelationship }
    ]);

    // A goal reads by its name on the profile; the context questions beside it read by their key.
    addSection('Goals and Family Context', Object.entries(factFind.goals || {}).map(([key, value]) => ({
        label: GOAL_LABELS[key] || prettify(key),
        value
    })));

    addSection('Home and Property Inputs', [
        { label: 'Home Value', value: factFind.home?.value },
        { label: 'Estimated Rent (Weekly)', value: factFind.home?.likelyRent },
        { label: 'Estimated Rental Expenses (Annual)', value: factFind.home?.likelyExpenses },
        { label: 'Home Growth Rate', value: factFind.home?.homeGrowthRate },
        { label: 'Current Residency', value: (factFind.home as Record<string, unknown> | undefined)?.currentResidency },
        { label: 'Land Size (>2 hectares)', value: factFind.home?.landSizeValue || factFind.home?.landSize },
        { label: 'Separate Titles', value: factFind.home?.separateTitlesValue || factFind.home?.separateTitles },
        { label: 'Primary Production Use', value: factFind.home?.primaryProductionUse },
        { label: 'Business Use', value: factFind.home?.businessUse },
        { label: 'Protected Person', value: factFind.home?.protectedPerson }
    ]);

    addSection('Expenses, Capital and Gifting Inputs', [
        { label: 'Annual Living Expenses', value: factFind.expenses?.livingExpenses },
        { label: 'Will Reduce Expenses After Care', value: expensesLegacy.willReduceExpensesAfterCare },
        { label: 'Reduced Living Expenses After Care', value: expensesLegacy.reducedLivingExpensesAfterCare },
        { label: 'Expense Mode', value: factFind.expenses?.expenseMode },
        { label: 'Capital Expenses Entered', value: (factFind.expenses?.capitalExpenses || []).length > 0 ? 'Yes' : 'No' },
        { label: 'Gifts History Entered', value: Object.keys(factFind.giftsHistory || {}).length > 0 ? 'Yes' : 'No' }
    ]);

    const clientName = `${factFind.client?.firstName || 'Client'}`.trim() || 'Primary applicant';
    const partnerName = `${factFind.partner?.firstName || 'Partner'}`.trim() || 'Partner';
    addSection('Care Need Inputs (aged care needs assessment)', [
        ...buildAcatFactRows(factFind.careNeeds?.clientAcat, clientName),
        ...buildAcatFactRows(factFind.careNeeds?.partnerAcat, partnerName)
    ]);

    return sections;
}

function extractRadFundingRows(radFunding: RadFundingBag = {}, factFind: DraftFactFind = {} as DraftFactFind): { source: string; amount: number }[] {
    return Object.entries(radFunding || {}).flatMap(([assetType, liquidations]) => {
        const items = Array.isArray(liquidations) ? liquidations : (liquidations ? [liquidations] : []);
        return items.map((liq) => {
            const sourceAsset = (factFind?.assets?.[assetType] || []).find((asset) => asset.id === liq.id);
            return {
                source: sanitizeText(sourceAsset?.description || liq.source || `${prettify(assetType)} Asset`),
                amount: toNumber(liq.amount)
            };
        });
    });
}

interface SoaOptimalCard {
    cardKind: 'partner_later' | 'lower_rad';
    objectiveLabel: string;
    name: string;
    narrative: string;
    legacy: number;
    legacyMetricLabel: string;
    cashflow: number;
    pension: number;
    selected: boolean;
    fundingRows: { source: string; amount: number }[];
    /** The horizon warning the card carries on screen — the same words, from the same rule. */
    horizonWarning?: string | null;
}

interface SoaCoupleValidation {
    downsizeNewHomeValue?: number;
    enablePartnerFutureEvent?: boolean;
    partnerFutureRoomPrice?: number | string;
    partnerEntryYear?: number | string;
    originalEntrantAliveAtPartnerEntry?: boolean;
    [key: string]: unknown;
}

interface BuildConditionalOptimalCardsForSoaParams {
    allScenarios: SoaScenario[];
    coupleValidation?: SoaCoupleValidation;
    agedCareCosts?: Record<string, unknown> | null;
    legacyHorizonYears?: number;
    selectedStrategy?: SoaScenario;
    factFind: DraftFactFind;
    formatCurrency: (value: number) => string;
}

/**
 * Optional SOA cards aligned with the Suggestion Engine: partner-later lens and Lower RAD near-best (baseline).
 * Copy is fixed (D2C-safe) so the document stands alone without adviser “reason not taken” fields.
 */
function buildConditionalOptimalCardsForSoa({
    allScenarios,
    coupleValidation,
    agedCareCosts,
    legacyHorizonYears,
    selectedStrategy,
    factFind,
    formatCurrency: formatMoney
}: BuildConditionalOptimalCardsForSoaParams): SoaOptimalCard[] {
    if (!Array.isArray(allScenarios) || allScenarios.length === 0) return [];

    const selNorm = stripFundingSuffix(selectedStrategy?.name || '');
    const cards: SoaOptimalCard[] = [];
    const cv = coupleValidation || {};
    const horizonYears = legacyHorizonYears || 5;

    if (cv.enablePartnerFutureEvent && parseFloat(String(cv.partnerFutureRoomPrice)) > 0) {
        const partnerEntryYear = parseInt(String(cv.partnerEntryYear), 10) || 3;
        const partnerFutureRoomPrice = parseFloat(String(cv.partnerFutureRoomPrice)) || 0;
        const originalEntrantAliveAtPartnerEntry = !!cv.originalEntrantAliveAtPartnerEntry;

        const scored = allScenarios
            .map((strategy) => {
                const result = computeFutureEventAdjustedLegacy({
                    strategy: strategy as unknown as EvaluatedStrategy,
                    legacyYears: horizonYears,
                    partnerEntryYear,
                    partnerRoomPrice: partnerFutureRoomPrice,
                    originalEntrantAliveAtEvent: originalEntrantAliveAtPartnerEntry,
                    baseAgedCareCosts: (strategy.strategySpecificAgedCareCosts || agedCareCosts) as Record<string, unknown>
                });
                if (!result) return null;
                return {
                    strategy,
                    result,
                    adjustedLegacy: result.adjustedLegacy,
                    radPaid: estimateRadPaidForStrategy(strategy as unknown as EvaluatedStrategy)
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);

        if (scored.length > 0) {
            const maxAdjustedLegacy = scored.reduce((m, row) => Math.max(m, row.adjustedLegacy), -Infinity);
            const bandUsd = SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD;
            const threshold = maxAdjustedLegacy - bandUsd;
            const nearBest = scored.filter((row) => row.adjustedLegacy >= threshold);
            const topAdjustedRow = scored.reduce((best, row) => (row.adjustedLegacy > best.adjustedLegacy ? row : best), scored[0]!);
            const pickedRow = nearBest.reduce((best, row) => {
                if (row.radPaid < best.radPaid) return row;
                if (row.radPaid === best.radPaid && row.adjustedLegacy > best.adjustedLegacy) return row;
                return best;
            }, nearBest[0]!);
            const usedLowerRadTieBreak = pickedRow.strategy?.name !== topAdjustedRow.strategy?.name;
            const st = pickedRow.strategy;

            const clientFirstName = sanitizeText(factFind?.client?.firstName || '').trim() || 'the client';
            const partnerFirstName = sanitizeText(factFind?.partner?.firstName || '').trim() || 'your partner';
            const clientAlivePhrase = originalEntrantAliveAtPartnerEntry
                ? 'still living in residential aged care'
                : 'having passed away';

            cards.push({
                cardKind: 'partner_later',
                objectiveLabel: usedLowerRadTieBreak
                    ? 'Optimal Legacy (Partner Later) — lower RAD option'
                    : 'Optimal Legacy (Partner Later Scenario)',
                name: sanitizeText(stripFundingSuffix(st.name)),
                narrative:
                    `This strategy considers ${partnerFirstName} entering residential aged care in year ${partnerEntryYear} at a room price of ${formatMoney(partnerFutureRoomPrice)}, with ${clientFirstName} modelled as ${clientAlivePhrase} at that time. ` +
                    `The tool estimates extra aged care costs from that year onward and subtracts them from each strategy's baseline projected legacy to rank outcomes. ` +
                    `Where several strategies finish within ${formatMoney(bandUsd)} of the best future-aware projected legacy, this card shows the one with the lowest estimated RAD paid. ` +
                    `Year 1 cashflow and Age Pension below use baseline (Comparison Yr1) figures for that strategy—they are not re-run under this future-event path. Illustrative only.`,
                legacy: pickedRow.adjustedLegacy,
                legacyMetricLabel: 'Legacy (future-event scenario)',
                cashflow: st.snapshot?.netCashflow ?? 0,
                pension: st.snapshot?.pension ?? 0,
                selected: selNorm === stripFundingSuffix(st.name),
                fundingRows: extractRadFundingRows(st.radFunding, factFind)
            });
        }
    }

    const pickedLower = pickLowerRadNearBestStrategy(allScenarios as unknown as EvaluatedStrategy[], SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD);
    if (pickedLower?.strategy) {
        const st = pickedLower.strategy;
        const lowerSelected = selNorm === stripFundingSuffix(st.name);
        const lowerRadCore =
            `This option models a scenario where the projected legacy value is within ${formatMoney(SUGGESTION_ENGINE_NEAR_BEST_LEGACY_BAND_USD)} of the highest legacy outcome while using a lower estimated RAD paid (i.e. less capital is locked in the refundable deposit). ` +
            `This could be an ideal strategy to consider where you use less of your money to get a similar projected estate value. `;
        const lowerRadClosing = lowerSelected
            ? `In your case, you selected this strategy for this Statement of Advice.`
            : `In your case, you have not selected this strategy.`;
        cards.push({
            cardKind: 'lower_rad',
            objectiveLabel: LOWER_RAD_NEAR_BEST_LEGACY_BADGE,
            name: sanitizeText(stripFundingSuffix(st.name)),
            narrative: `${lowerRadCore}${lowerRadClosing}`,
            legacy: Number(st.snapshot?.legacyValue) || 0,
            legacyMetricLabel: 'Legacy (modelled)',
            cashflow: Number(st.snapshot?.netCashflow) || 0,
            pension: Number(st.snapshot?.pension) || 0,
            selected: lowerSelected,
            fundingRows: extractRadFundingRows(st.radFunding, factFind)
        });
    }

    return cards;
}

interface BuildAgedCareDigitalSOASnapshotParams {
    selectedStrategy: SoaScenario;
    factFind: DraftFactFind;
    agedCareCosts?: ({ radAmount?: number } & Record<string, unknown>) | null;
    analysis?: SoaAnalysis | null;
    coupleValidation?: SoaCoupleValidation;
    reportAudience?: string;
    strategySource?: string;
    manualAlternativeStrategies?: SoaScenario[];
    /**
     * The period the supplied `projections` were actually CUT ON — not the reader's preference.
     *
     * `projectionPeriodMode` is a modelling choice, not a display filter (the engine opens, grows and
     * settles on the period it reports), so this document can only describe the calendar its own rows
     * came from. A caller that can re-run the engine on the reader's chosen period passes the new rows
     * AND this; a caller that cannot passes neither, and the document truthfully describes the
     * financial-year rows it was given. Never pass a mode the rows do not match.
     */
    projectionPeriodMode?: string;
    /**
     * Who this advice comes from, already resolved into its compliance line
     * (professionalPortal/adviceAttribution.ts).
     *
     * Passed in rather than worked out here: which entities a document must name depends on how the
     * FIRM is licensed, which this module has no way to know, and the cover has been carrying a
     * hardcoded demo licence in its place. Absent for a consumer working on their own file, where
     * there is no adviser and no firm — the cover then falls back to the placeholder it has always
     * shown, which at least says "(Demo)" out loud.
     */
    adviceAttributionLine?: string;
    /** True when that line is missing an entity the structure requires — see adviceAttribution(). */
    adviceAttributionIncomplete?: boolean;
}

export async function buildAgedCareDigitalSOASnapshot({
    selectedStrategy,
    factFind,
    agedCareCosts,
    analysis,
    coupleValidation = {},
    reportAudience = 'direct_client',
    strategySource = 'suggestion_engine',
    manualAlternativeStrategies = [],
    projectionPeriodMode,
    adviceAttributionLine,
    adviceAttributionIncomplete
}: BuildAgedCareDigitalSOASnapshotParams) {
    if (!selectedStrategy) return null;

    const baseline = await calculationApi.calculateFinancials(factFind || {});
    const allScenarios: SoaScenario[] = analysis?.allSimulatedStrategies || analysis?.strategies || [];
    const projections = selectedStrategy.projections || [];
    // Falls back to the strategy's own run options, so a caller that says nothing still gets a document
    // describing the calendar its rows were genuinely cut on rather than an assumed one.
    // Entry and the funding sale are separate dated events, and the gap between them is modelled —
    // proceeds sit in the bank earning deemed income until the deposit is paid. The document has to
    // report the dates that were actually chosen, not assume the two coincide.
    const strategyRunOptions = (selectedStrategy.strategyOptions || {}) as Record<string, unknown>;
    const careStartDate = typeof strategyRunOptions.careStartDate === 'string' ? strategyRunOptions.careStartDate : '';
    const radFundingDate = typeof strategyRunOptions.radFundingDate === 'string' ? strategyRunOptions.radFundingDate : '';
    // Whether the provider lets fees be drawn from the deposit, as answered on the costs form. Absent
    // on strategies from before the question existed, which ran on the usual arrangement ('fees').
    const radDrawdown: 'fees' | 'dap' | 'none' =
        strategyRunOptions.radDrawdown === 'dap' || strategyRunOptions.radDrawdown === 'none' ? strategyRunOptions.radDrawdown : 'fees';
    // The fee arrangements the run was priced on. The profile decides (no-worse-off principle); the
    // run's options can only record an opt-in. Resolved by the engine's own function so the document
    // cannot say one thing and the fees another.
    const coveredByNoWorseOff = householdCoveredByNoWorseOffPrinciple(factFind as unknown as FactFind);
    const residentialFeeArrangements: '2014' | '2025' = coveredByNoWorseOff && strategyRunOptions.residentialFeeArrangements !== '2025' ? '2014' : '2025';
    const rowPeriodMode = projectionPeriodMode
        ?? (selectedStrategy.strategyOptions as Record<string, unknown> | undefined)?.projectionPeriodMode as string | undefined
        ?? 'financial_year';
    // Falls back to whichever firm is signed in when the caller did not say. See
    // adviceAttribution.ts for why that lives in a module value rather than a prop.
    const currentAttribution = getCurrentAdviceAttribution();
    const attributionLine = adviceAttributionLine ?? currentAttribution?.line;
    const attributionIncomplete = adviceAttributionIncomplete ?? currentAttribution?.complete === false;

    const reportId = `CC-DSOA-${Date.now()}`;
    const generatedAt = new Date().toISOString();

    // Actual RAD paid = funding NET of CGT on any assets sold to fund it (Option A). The SE strategy carries
    // this as radPaidNet (same figure its projection/DAP use); fall back to the gross funding sum for other
    // sources. Using the gross here previously OVERSTATED the RAD and UNDERSTATED the DAP on the unpaid balance.
    const grossRadFunding = Object.values(selectedStrategy.radFunding || {})
        .flatMap((items) => (Array.isArray(items) ? items : (items ? [items] : [])))
        .reduce((sum, row) => sum + toNumber(row.amount), 0);
    const totalRadPaid: number = Number.isFinite(selectedStrategy.radPaidNet)
        ? selectedStrategy.radPaidNet!
        : grossRadFunding;

    const roomCost = toNumber(agedCareCosts?.radAmount);
    const remainingForDap = Math.max(0, roomCost - totalRadPaid);
    const mpir = AGED_CARE_SCHEDULE.MPIR;

    const safeFactFind = factFind || ({} as DraftFactFind);
    const modellerResidencyOverrides = agedCareEntrantResidencyOverrides(asFactFind(safeFactFind));
    const pensionScenarioOptions = {
        ...(selectedStrategy?.strategyOptions || {}),
        ...(selectedStrategy?.scenarioConfig || {}),
        ...modellerResidencyOverrides
    };
    const baselineUsesIllnessSeparated = isIllnessSeparatedCouple(asFactFind(safeFactFind), {});
    const modelUsesIllnessSeparated = isIllnessSeparatedCouple(asFactFind(safeFactFind), pensionScenarioOptions);
    const illnessSeparatedAppliesInSoa = safeFactFind.adviceFor === 'couple' && modelUsesIllnessSeparated;
    const illnessSeparatedDiffersFromBaseline = safeFactFind.adviceFor === 'couple'
        && baselineUsesIllnessSeparated !== modelUsesIllnessSeparated;
    const baselineAnnualPension = baseline.socialSecurity || baseline.agePension || 0;
    const selectedAnnualPension = selectedStrategy.snapshot?.pension || 0;
    const pensionDeltaFromBaseline = selectedAnnualPension - baselineAnnualPension;
    const localClientFirstName = sanitizeText(safeFactFind.client?.firstName || 'the client');
    const localPartnerFirstName = sanitizeText(safeFactFind.partner?.firstName || '');
    const localIsCoupleAdvice = safeFactFind.adviceFor === 'couple' && localPartnerFirstName;
    const agedCareFor = safeFactFind?.goals?.aged_care_for;
    const enteringCarePersonName = (() => {
        if (agedCareFor === 'partner') return localPartnerFirstName || 'your partner';
        if (agedCareFor === 'both') return localIsCoupleAdvice && localPartnerFirstName
            ? `${localClientFirstName} and ${localPartnerFirstName}`
            : localClientFirstName;
        return localClientFirstName;
    })();
    const baselinePensionMax = (() => {
        if (safeFactFind?.adviceFor !== 'couple') return PENSION_RATES.single;
        return baselineUsesIllnessSeparated ? PENSION_RATES.single * 2 : PENSION_RATES.couple;
    })();
    const selectedPensionMax = (() => {
        if (safeFactFind?.adviceFor !== 'couple') return PENSION_RATES.single;
        return modelUsesIllnessSeparated ? PENSION_RATES.single * 2 : PENSION_RATES.couple;
    })();
    const baselinePensionStatus = classifyPensionLevel(baselineAnnualPension, baselinePensionMax);
    const selectedPensionStatus = classifyPensionLevel(selectedAnnualPension, selectedPensionMax);
    const recommendedNarrative = sanitizeText(
        selectedStrategy.context || selectedStrategy.description || 'Recommended strategy based on current modelling.'
    );

    const topThree = allScenarios
        .slice()
        .sort((a, b) => (b.snapshot?.legacyValue || 0) - (a.snapshot?.legacyValue || 0))
        .slice(0, 3)
        .map((strategy) => ({
            name: sanitizeText(strategy.name),
            legacy: strategy.snapshot?.legacyValue || strategy.outputs?.legacyEstimate || 0,
            cashflow: strategy.snapshot?.netCashflow || strategy.cashFlow || strategy.cashflow || 0,
            pension: strategy.snapshot?.pension || strategy.agePension || strategy.estimatedAgePension || 0,
            selected: strategy.name === selectedStrategy.name
        }));

    const optimalStrategyCards = (analysis?.recommendedOptimalStrategies || [])
        .filter((row) => row?.strategy)
        .slice(0, 3)
        .map((row) => {
            const st = row.strategy;
            return {
                objectiveLabel: sanitizeText(row.label),
                name: sanitizeText(stripFundingSuffix(st.name)),
                narrative: sanitizeText(st.context || st.description || ''),
                legacy: st.snapshot?.legacyValue || st.outputs?.legacyEstimate || 0,
                cashflow: st.snapshot?.netCashflow || st.cashFlow || st.cashflow || 0,
                pension: st.snapshot?.pension || st.agePension || st.estimatedAgePension || 0,
                selected: st.name === selectedStrategy.name,
                fundingRows: extractRadFundingRows(st.radFunding, factFind),
                horizonWarning: engineStrategyHorizonWarning(st as never, factFind as never),
            };
        });

    const manualCards = (manualAlternativeStrategies || [])
        .slice(0, 6)
        .map((st, idx) => ({
            objectiveLabel: `Modelled strategy ${idx + 1}`,
            name: sanitizeText(stripFundingSuffix(st.name || `Strategy ${idx + 1}`)),
            narrative: sanitizeText(st.context || st.description || 'Saved during adviser manual-control modelling.'),
            legacy: st.snapshot?.legacyValue || st.outputs?.legacyEstimate || 0,
            cashflow: st.snapshot?.netCashflow || st.outputs?.cashFlow || st.cashFlow || st.cashflow || 0,
            pension: st.snapshot?.pension || st.outputs?.agePension || st.agePension || st.estimatedAgePension || 0,
            selected: stripFundingSuffix(st.name || '') === stripFundingSuffix(selectedStrategy?.name || ''),
            fundingRows: extractRadFundingRows(st.radFunding || st.inputs?.radFunding, factFind),
        }));

    const displayOptimalCards = strategySource === 'manual_control'
        ? (manualCards.length > 0 ? manualCards : [{
            objectiveLabel: 'Selected manual strategy',
            name: sanitizeText(stripFundingSuffix(selectedStrategy?.name || 'Manual strategy')),
            narrative: 'Selected by adviser from manual control modelling.',
            legacy: selectedStrategy.snapshot?.legacyValue || 0,
            cashflow: selectedStrategy.snapshot?.netCashflow || 0,
            pension: selectedStrategy.snapshot?.pension || 0,
            selected: true,
            fundingRows: extractRadFundingRows(selectedStrategy.radFunding, factFind),
        }])
        : (optimalStrategyCards.length > 0
            ? optimalStrategyCards
            : topThree.map((row, idx) => ({
                  objectiveLabel: ['Strongest projected estate', 'Cashflow-led outcome', 'Age Pension-led outcome'][idx] || 'Modelling highlight',
                  name: sanitizeText(stripFundingSuffix(row.name)),
                  narrative: row.selected ? recommendedNarrative : 'Alternative mix of RAD/DAP, home and asset funding assumptions.',
                  legacy: row.legacy,
                  cashflow: row.cashflow,
                  pension: row.pension,
                  selected: row.selected,
                  fundingRows: []
              })));

    const legacyHorizonYears = parseInt(String(safeFactFind.goals?.legacyYears), 10) || 5;
    // The situation page's fourth tile: the household staying where it is, no care, over the same
    // horizon (owner, 9 Oct 2026). Null where someone is already in care or the run fails.
    const stayPutLegacy = await stayPutEstate(factFind, legacyHorizonYears, { ...(projectionPeriodMode ? { projectionPeriodMode } : {}) });
    // The Age Pension page's home treatment, from the arrangement the person entering care is in.
    const careEntrantKey = agedCareFor === 'partner' ? 'partner' : 'client';
    const careEntrantArrangement = (careEntrantKey === 'partner' ? safeFactFind.partner?.currentResidency : safeFactFind.client?.currentResidency) || 'own_home';
    const stayingPartnerArrangement = (careEntrantKey === 'partner' ? safeFactFind.client?.currentResidency : safeFactFind.partner?.currentResidency) || 'own_home';
    const partnerStaysHome = !!localIsCoupleAdvice && agedCareFor !== 'both' && stayingPartnerArrangement !== 'aged_care';
    const homeRuleParagraph = homeRuleWords({
        arrangement: careEntrantArrangement,
        name: sanitizeText(careEntrantKey === 'partner' ? localPartnerFirstName || 'your partner' : localClientFirstName),
        partnerName: sanitizeText((careEntrantKey === 'partner' ? localClientFirstName : localPartnerFirstName) || ''),
        partnerStays: partnerStaysHome,
        partnerArrangement: stayingPartnerArrangement,
    });
    const conditionalOptimalCards =
        strategySource === 'suggestion_engine'
            ? buildConditionalOptimalCardsForSoa({
                  allScenarios,
                  coupleValidation,
                  agedCareCosts,
                  legacyHorizonYears,
                  selectedStrategy,
                  factFind: safeFactFind,
                  formatCurrency
              })
            : [];

    const selectedGoalKeys = Object.entries(safeFactFind.goals || {})
        .filter(([key, val]) => val && !key.endsWith('_for') && GOAL_LABELS[key])
        .map(([key]) => key);
    const statedGoals = selectedGoalKeys.map((key) => GOAL_LABELS[key]);
    const inScopeGoalKeys = selectedGoalKeys.filter((key) => !LIVING_ARRANGEMENT_GOAL_KEYS.has(key) || key === 'aged_care');
    const outOfScopeGoalKeys = selectedGoalKeys.filter((key) => LIVING_ARRANGEMENT_GOAL_KEYS.has(key) && key !== 'aged_care');
    if (!inScopeGoalKeys.includes('aged_care')) inScopeGoalKeys.unshift('aged_care');
    const inScopeGoals = inScopeGoalKeys.map((key) => GOAL_LABELS[key]).filter(Boolean);
    const outOfScopeGoals = outOfScopeGoalKeys.map((key) => GOAL_LABELS[key]).filter(Boolean);

    const scenarioHints: string[] = [];
    const g = safeFactFind.goals || {};
    if (g.downsize_home) scenarioHints.push('downsizing the home');
    if (g.granny_flat) scenarioHints.push('granny flat arrangements');
    if (g.retirement_village) scenarioHints.push('retirement village pathways');
    if (g.home_care) scenarioHints.push('home care package transitions');
    if ((coupleValidation?.downsizeNewHomeValue || 0) > 0) scenarioHints.push('partner downsizing alternatives');
    if (coupleValidation?.partnerAnnualRent) scenarioHints.push('partner rental assumptions');

    const totalScenariosCount = analysis?.totalScenarios || allScenarios.length || 0;
    const scenarioExplainer = strategySource === 'manual_control'
        ? `This advice references <strong>${manualCards.length || totalScenariosCount || 1}</strong> strategy paths modelled in Manual Control and saved for comparison.`
        : `We compared <strong>${totalScenariosCount}</strong> strategy combinations, including different ways to use the family home (keep, rent, sell or downsize where relevant), RAD and DAP mixes, and how assets could fund care—aligned with your fact-find and the options you told us matter${scenarioHints.length ? ` (for example: ${scenarioHints.join(', ')})` : ''}.`;

    const doNothingStrategy = allScenarios.find((s) => String(s.name || '').trim().toLowerCase() === 'do nothing');
    let pathComparison = null;
    if (doNothingStrategy?.snapshot && selectedStrategy?.snapshot) {
        const sel = selectedStrategy.snapshot;
        const dn = doNothingStrategy.snapshot;
        pathComparison = {
            legacyDelta: (sel.legacyValue || 0) - (dn.legacyValue || 0),
            pensionDelta: (sel.pension || 0) - (dn.pension || 0),
            cashflowDelta: (sel.netCashflow || 0) - (dn.netCashflow || 0),
            /** Positive => selected strategy has higher modelled aged care fees than "do nothing" */
            careFeeDeltaVsDoNothing: (sel.totalAgedCareCosts || 0) - (dn.totalAgedCareCosts || 0)
        };
    }

    const annualCashflow = selectedStrategy.snapshot?.netCashflow || selectedStrategy.cashFlow || selectedStrategy.cashflow || 0;
    const cashflowIsNegative = annualCashflow < 0;
    const cashflowAnalysis = analyzeMultiYearCashflowForSOA(projections, formatCurrency);

    let deficitFundingNarrative = '';
    if (cashflowIsNegative) {
        const baseExplain = getDeficitFundingExplanationForSOA(annualCashflow, projections, formatCurrency);
        const layered = cashflowAnalysis?.detailedNarrative;
        const parts: string[] = [];
        if (baseExplain) parts.push(baseExplain);
        if (layered && layered !== baseExplain) parts.push(layered);
        deficitFundingNarrative = parts.join(' ');
    }
    /*
     * HOW LONG THE MONEY LASTS — the same helper, over the same rows and horizon, that the plan
     * summary panel uses on screen, so the document cannot answer the question differently from
     * the screen it was generated from (the "one runway, quoted twice" rule in AgedCareAnalyser).
     */
    const fundingRunway = cashflowIsNegative && projections.length
        ? planRunwayCopy(projections as unknown as FundingProjectionYear[], projections.length)
        : null;

    const projectionSurplusDeficitNote = buildProjectionFootnote(cashflowAnalysis, annualCashflow, formatCurrency);

    const enrichedProjections = projections.map((p, i) => ({
        year: p.year ?? i + 1,
        fyLabel: p.fyLabel,
        isPartialFy: p.isPartialFy,
        fyFullYearView: p.fyFullYearView,
        cashFlow: p.cashFlow ?? p.cashflow ?? 0,
        netWorth: p.netWorth ?? 0,
        assets: p.assets || {},
        netWorthBreakdown: p.netWorthBreakdown || [],
        pension: p.socialSecurity ?? p.pension ?? 0,
        careAnnual: p.annualCareCost ?? 0,
        cashBreakdown: p.cashFlowBreakdown || [],
        careBreakdown: p.agedCareBreakdown || [],
        pensionBreakdown: p.socialSecurityBreakdown || [],
        drawdownBreakdown: p.drawdownBreakdown || []
    }));

    const strategyOpeningNetWorth =
        selectedStrategy.snapshot != null && selectedStrategy.snapshot.netWorth != null && selectedStrategy.snapshot.netWorth !== ''
            ? toNumber(selectedStrategy.snapshot.netWorth)
            : toNumber(baseline.netWorth);
    const openingNetWorthTipRows = buildOpeningNetWorthBreakdownLines(projections[0], strategyOpeningNetWorth, safeFactFind);

    // Use the same scenario structures as the app deep-dive:
    // - do-nothing from `analysis.strategies`
    // - selected strategy from the selected strategy payload
    const doNothing = analysis?.strategies?.find((s) => String(s?.name || '').trim().toLowerCase() === 'do nothing') ||
        allScenarios.find((s) => String(s?.name || '').trim().toLowerCase() === 'do nothing');
    const selectedScenarioForOpportunity = selectedStrategy;
    const selectedSnap = selectedStrategy?.snapshot || {};
    const doNothingSnap = doNothing?.snapshot || {};
    const investmentEarningsLost = doNothing
        ? estimateInvestmentEarningsLikeApp(selectedScenarioForOpportunity, doNothing)
        : 0;
    let careCostsSaved = toNumber(doNothingSnap.totalAgedCareCosts) - toNumber(selectedSnap.totalAgedCareCosts);
    // If the snapshot care costs look identical, the app falls back to estimating DAP savings from RAD.
    if (careCostsSaved === 0 && toNumber(selectedScenarioForOpportunity?.radAmount) > 0) {
        careCostsSaved = toNumber(selectedScenarioForOpportunity?.radAmount) * mpir;
    }

    const opportunityCost = doNothing
        ? {
              investmentEarningsLost,
              agePensionImpact: toNumber(selectedSnap.pension) - toNumber(doNothingSnap.pension),
              careCostsSaved,
              estateImpact: toNumber(selectedSnap.legacyValue) - toNumber(doNothingSnap.legacyValue)
          }
        : null;

    const clientFirstName = sanitizeText(safeFactFind.client?.firstName || 'the client');
    const partnerFirstName = sanitizeText(safeFactFind.partner?.firstName || '');
    const isCoupleAdvice = safeFactFind.adviceFor === 'couple' && partnerFirstName;
    const completerIsApplicant = !safeFactFind.completer || safeFactFind.completer === 'client';
    const completerRelationship = sanitizeText(safeFactFind.completerRelationship || '');
    const completerRelationshipLower = completerRelationship
        ? `${completerRelationship.charAt(0).toLowerCase()}${completerRelationship.slice(1)}`
        : '';
    const familyEstate = safeFactFind.family || {};

    const clientFullName = sanitizeText(
        `${safeFactFind.client?.firstName || ''} ${safeFactFind.client?.lastName || ''}`.trim() || 'the client'
    );
    const partnerLastName = sanitizeText(safeFactFind.partner?.lastName || '');
    const partnerFullName =
        partnerFirstName && partnerLastName
            ? `${partnerFirstName} ${partnerLastName}`.trim()
            : partnerFirstName || '';
    const clientAgeNum = calculateAge(safeFactFind.client?.dob);
    const clientAgeStr =
        clientAgeNum != null && !Number.isNaN(Number(clientAgeNum)) ? String(clientAgeNum) : null;
    const partnerAgeNum = partnerFullName ? calculateAge(safeFactFind.partner?.dob) : null;
    const partnerAgeStr =
        partnerAgeNum != null && !Number.isNaN(Number(partnerAgeNum)) ? String(partnerAgeNum) : null;

    const executiveSummaryOpeningParts = [];
    if (isCoupleAdvice && partnerFullName) {
        executiveSummaryOpeningParts.push(
            `<p class="lede">This Statement of Advice has been prepared for <strong>${clientFullName}</strong>${clientAgeStr ? ` (aged ${clientAgeStr})` : ''} and <strong>${sanitizeText(partnerFullName)}</strong>${partnerAgeStr ? ` (aged ${partnerAgeStr})` : ''}.</p>`
        );
    } else {
        executiveSummaryOpeningParts.push(
            `<p class="lede">This Statement of Advice has been prepared for <strong>${clientFullName}</strong>${clientAgeStr ? ` (aged ${clientAgeStr})` : ''}.</p>`
        );
    }

    if (!completerIsApplicant) {
        const completerFullName = sanitizeText(
            `${safeFactFind.completerFirstName || ''} ${safeFactFind.completerLastName || ''}`.trim()
        );
        const behalf = isCoupleAdvice ? 'the clients named above' : 'the client named above';
        if (completerFullName) {
            const relFragment = completerRelationship
                ? ` (${clientFirstName}'s ${completerRelationshipLower})`
                : '';
            executiveSummaryOpeningParts.push(
                `<p class="lede">It was completed using information provided by <strong>${completerFullName}</strong>${relFragment}, on behalf of ${behalf}.</p>`
            );
        } else {
            executiveSummaryOpeningParts.push(
                `<p class="lede">It was completed on behalf of ${isCoupleAdvice ? 'the clients' : 'the client'} named above.</p>`
            );
        }
    }

    const executiveSummaryOpeningHtml = executiveSummaryOpeningParts.join('');

    const clientAgeForSituation = calculateAge(safeFactFind.client?.dob);
    const ageFragment =
        clientAgeForSituation != null && !Number.isNaN(Number(clientAgeForSituation))
            ? ` at age <strong>${clientAgeForSituation}</strong>`
            : '';
    /*
     * WHAT WAS DONE WITH THE HEALTH ANSWER (owner, 23 Sep 2026). This document used to print the
     * answer as a bare fact and stop. It now says what the answer was used for — or that nothing was,
     * where it was left blank — so the reader is never left to wonder whether a relevant circumstance
     * was considered.
     */
    const horizons = personHorizons(safeFactFind as never);
    const clientHealthRaw = String(safeFactFind.client?.healthStatus ?? '').trim();
    const horizonConsequence = !clientHealthRaw
        ? ` No health status was recorded, so no judgement about time horizon was made on it; age alone was considered.`
        : horizons.client.healthConsidered
            ? ` Age and this health answer were taken into account: strategies that need several years or more to pay for themselves were flagged rather than recommended.`
            : ` Age and this health answer were taken into account when judging which strategies had the time they need.`;
    const careRecipientProfile = `<strong>${clientFirstName}</strong> is entering, or preparing to enter, residential aged care${ageFragment}, and reports <strong>${healthStatusLabel(safeFactFind.client?.healthStatus)}</strong>.${horizonConsequence}`;
    // A strategy that would have won a ranking but was set aside is said here, in the document,
    // exactly as it is said on screen (owner, 23 Sep 2026: call it out, never promote it).
    const setAsides = ((analysis as { horizonSetAside?: HorizonSetAside<{ name?: string }>[] } | null | undefined)?.horizonSetAside ?? []);
    const horizonSetAsideHtml = setAsides.length
        ? `<div class="callout callout--amber"><p class="callout-title">Set aside because of age and health</p>${setAsides.map((sa) =>
            `<p><strong>${sanitizeText(stripFundingSuffix(sa.strategy?.name || ''))}</strong> would give ${sa.metric === 'legacy' ? 'a larger estate' : sa.metric === 'cashflow' ? 'a higher cashflow' : 'a higher Age Pension'} than the ${sanitizeText(sa.label)} strategy above, but it was not recommended: ${sanitizeText(sa.warning)}</p>`).join('')}</div>`
        : '';

    const partnerContextParagraphs: string[] = [];
    if (isCoupleAdvice && partnerFirstName) {
        const partnerHealth = healthStatusLabel(safeFactFind.partner?.healthStatus);
        partnerContextParagraphs.push(
            `<p class="lede"><strong>${partnerFirstName}</strong>'s health was recorded as <strong>${partnerHealth}</strong>. Where the family home is retained, the model uses the living arrangement and cost settings you entered (including whether the partner remains in the home). The home is treated as exempt for the assets test while the partner remains a continuing occupant.</p>`
        );
        const partnerExtraBits: string[] = [];
        if ((coupleValidation?.downsizeNewHomeValue || 0) > 0 || g.downsize_home) {
            partnerExtraBits.push(
                'The comparison also included scenarios where the partner downsizes or changes living arrangements while aged care funding is implemented.'
            );
        }
        const homeRentalModelled = Boolean(
            selectedStrategy?.scenarioConfig?.rentHome || selectedStrategy?.snapshot?.homeStatus === 'Rent Home'
        );
        if (homeRentalModelled) {
            partnerExtraBits.push(
                'Renting the family home was included in the comparison set, so housing-cost assumptions were adjusted for that pathway.'
            );
        }
        if (partnerExtraBits.length) {
            partnerContextParagraphs.push(`<p class="lede">${partnerExtraBits.join(' ')}</p>`);
        }
    }
    const partnerContextHtml = partnerContextParagraphs.join('');
    // The box itself sits with the alternatives (owner, 9 Oct 2026: the executive summary is not the
    // place to argue an annuity); the summary keeps one line saying something was set aside and where.
    const horizonSetAsideNote = setAsides.length ? horizonSetAsideSummaryLine(setAsides.length) : '';

    const wideExplorationClause =
        totalScenariosCount > 80
            ? `<p class="lede">Even where you had a preferred direction (for example keeping the home), AgedCareWise still tested a broad range of combinations - including RAD/DAP mixes and, where enabled, renting or downsizing - so you could see the trade-offs in pension, cashflow and estate outcomes before deciding.</p>`
            : '';

    const radFundingRows = Object.entries(selectedStrategy.radFunding || {}).flatMap(([assetType, liquidations]) => {
        const items = Array.isArray(liquidations) ? liquidations : (liquidations ? [liquidations] : []);
        return items.map((liq) => {
            const sourceAsset = (factFind?.assets?.[assetType] || []).find((asset) => asset.id === liq.id);
            return {
                assetTypeKey: assetType,
                assetId: liq.id,
                assetType: prettify(assetType),
                source: sanitizeText(sourceAsset?.description || liq.source || `${prettify(assetType)} Asset`),
                amount: toNumber(liq.amount)
            };
        });
    });
    /*
     * Money the plan puts to the deposit that was never sold at entry (owner-caught, 9 Oct 2026: the
     * funding list "does not include proceeds from the retirement village"): a village's exit payment
     * and the refund of a current room's deposit. Listed FIRST wherever the funding is listed — these
     * come before anything is sold — and kept apart from `radFundingRows`, which the CGT working reads.
     */
    const radFundingPrelude: Array<{ assetType: string; source: string; amount: number }> = (() => {
        const out: Array<{ assetType: string; source: string; amount: number }> = [];
        const veRow = (selectedStrategy as { villageExit?: { refundDate?: string; fromVillage?: number; paidAtMove?: boolean } | null }).villageExit;
        const veManual = (selectedStrategy.inputs as { villageExit?: { refundDate?: string; fromVillage?: number; paidAtMove?: boolean } | null } | undefined)?.villageExit;
        const ve = veRow && veRow.refundDate ? veRow : veManual && veManual.refundDate ? veManual : null;
        const fromVillage = Number(ve?.fromVillage) || 0;
        if (ve && fromVillage > 0) {
            out.push({
                assetType: 'Retirement village',
                source: ve.paidAtMove ? 'Exit payment for the village unit, paid at the move' : `Exit payment for the village unit, when it is paid (${refundByLabel({ refundDate: String(ve.refundDate) })})`,
                amount: fromVillage,
            });
        }
        const fmRow = (selectedStrategy as { facilityMove?: { stayPut?: boolean; fromRefund?: number } | null }).facilityMove;
        const fmManual = (selectedStrategy.inputs as { facilityMove?: { refundToday?: number; newRoomPrice?: number; radTarget?: number } | null } | undefined)?.facilityMove;
        const fromRefund = fmRow && !fmRow.stayPut
            ? Number(fmRow.fromRefund) || 0
            : fmManual && (Number(fmManual.newRoomPrice) || 0) > 0
              ? Math.min(Number(fmManual.refundToday) || 0, Math.min(Number(fmManual.newRoomPrice) || 0, Number(fmManual.radTarget) || 0))
              : 0;
        if (fromRefund > 0) {
            out.push({ assetType: 'Current room', source: 'Refund of the deposit on the current room', amount: fromRefund });
        }
        return out;
    })();
    // The CGT this strategy's RAD funding actually triggers, recomputed from the funding map itself.
    // It used to be read from `factFind.cgtOnRadFunding`, a sidecar that NOTHING in the codebase ever
    // writes — so it was always 0 and every piece of CGT commentary below (the per-asset cgtLine and
    // the "Tax validation required" card) silently never rendered, in a document whose whole purpose is
    // disclosure. `computeRadFundingTotals` is the same helper the RAD funding UI uses, so the figure
    // here matches what the adviser saw on screen.
    const radFundingCgtTotal = computeRadFundingTotals(
        factFind as unknown as FactFind,
        Object.fromEntries(radFundingRows.map((row) => [`${row.assetTypeKey}-${row.assetId}`, row.amount])),
        ((selectedStrategy?.strategyOptions as Record<string, unknown> | undefined)?.cgtOverrides as Record<string, unknown>) || {}
    ).shareCgtTotal;

    // CGT inputs we had to ASSUME for an asset this strategy sells (missing cost base / acquisition
    // month). Gated on the realised assets themselves rather than on the CGT total: an assumption still
    // has to be disclosed even where it happens to produce nil tax.
    const realisedCgtRefs: RealisedAssetRef[] = radFundingRows.map((row) => ({
        assetType: row.assetTypeKey,
        assetId: row.assetId
    }));
    const sellIpDetails = (selectedStrategy?.strategyOptions as Record<string, unknown> | undefined)?.investmentPropertyDetails as
        | Record<string, unknown>
        | undefined;
    if ((selectedStrategy?.strategyOptions as Record<string, unknown> | undefined)?.sellInvestmentProperty && sellIpDetails?.selectedPropertyId != null) {
        realisedCgtRefs.push({ assetType: 'investmentProperty', assetId: sellIpDetails.selectedPropertyId });
    }
    const cgtAssumptionDisclosure = buildCgtAssumptionDisclosure(
        findRealisedCgtDataGaps(factFind?.assets as unknown as FactFindAssetMap, realisedCgtRefs)
    );

    const adviceImplications = buildAdviceImplications({
        radFundingRows,
        radFundingCgtTotal,
        cgtAssumptionDisclosure,
        // The engine reports the terms it built the annuity on; the SOA states them.
        annuityTerms: (selectedStrategy as { annuityTerms?: Record<string, unknown> })?.annuityTerms || null,
        clientFirstName: safeFactFind.client?.firstName,
        partnerFirstName: safeFactFind.adviceFor === 'couple' ? safeFactFind.partner?.firstName : undefined
    }).map((item) => {
        if (item.title.toLowerCase().includes('account based pensions') && (calculateAge(safeFactFind.client?.dob) || 0) >= 60) {
            return {
                ...item,
                body: `${item.body} Because the member is over age 60 in this case, pension withdrawals are generally tax free (subject to product and legislative rules).`
            };
        }
        if (item.title.toLowerCase().includes('bank accounts')) {
            const bankTotal = (safeFactFind?.assets?.bankAccounts || []).reduce((sum, a) => sum + toNumber(a?.value), 0);
            const bankSold = radFundingRows
                .filter((r) => r.assetTypeKey === 'bankAccounts')
                .reduce((sum, r) => sum + toNumber(r.amount), 0);
            const retained = Math.max(0, bankTotal - bankSold);
            return {
                ...item,
                body: `${item.body} We have retained approximately <strong>${formatCurrency(retained)}</strong> in bank cash as an emergency buffer; this may reduce over time where ongoing deficits are projected.`
            };
        }
        return item;
    });

    /*
     * Claiming, or reporting, the pension change the plan rests on (owner, 3 Oct 2026). For aged
     * care the order matters too: claim the pension first, then the means assessment is the SA485.
     */
    const pensionNote = pensionCallout(selectedAnnualPension, baselineAnnualPension, { meansAssessment: 'residential' });
    const plainHtml = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (pensionNote) {
        adviceImplications.unshift({ title: plainHtml(pensionNote.implication.title), lead: plainHtml(pensionNote.implication.lead), body: plainHtml(pensionNote.implication.body) });
    }
    const pensionNextStep = pensionNote ? plainHtml(pensionNote.nextStep) : null;

    const assetRows = Object.entries(factFind?.assets || {}).flatMap(([assetType, assets]) =>
        (assets || []).map((asset) => {
            const figures = assetAssumptionFiguresFor(assetType, asset as unknown as Record<string, unknown>);
            const { incomeRate, growthRate } = figures;
            return {
                assetType: prettify(assetType),
                description: sanitizeText(asset.description || 'Unnamed Asset'),
                value: toNumber(asset.value),
                basis: figures.basis,
                incomeRate,
                growthRate,
                overridden: figures.overridden
            };
        })
    );

    /*
     * ASSUMPTIONS THE CLIENT CHANGED.
     *
     * A client may set the growth or income rate on an asset, as Moneysmart's planner lets them. The
     * document then has to say three things plainly: which figures are theirs, that they are not our
     * recommendation, and what the choice is worth — this plan at their rates against the same plan at
     * ours. Named as the CLIENT's decision whoever typed it: a referring firm cannot be seen to have
     * chosen an assumption on advice that is AgedCareWise's, and for an adviser firm the adviser's own
     * attribution is already on the cover (owner direction, 2026-09-12).
     */
    const rateOverrides = listAssetRateOverrides(factFind);
    const describeRun = (rows: SoaProjectionYear[] | null | undefined) => {
        if (!rows || !rows.length) return null;
        const funding = summariseFunding(rows as unknown as FundingProjectionYear[]);
        const last = rows[rows.length - 1];
        return {
            years: rows.length,
            savingsLast: funding.fundedFullPlan || funding.bankDepletesYear == null
                ? `the full ${rows.length}-year plan`
                : `about Year ${funding.bankDepletesYear}`,
            estate: toNumber(last?.netWorth),
        };
    };
    const assumptionsChanged = rateOverrides.length
        ? {
              clientName: sanitizeText(`${factFind?.client?.firstName || ''} ${factFind?.client?.lastName || ''}`.trim() || 'the client'),
              anyAbove: anyRateAboveDefault(rateOverrides),
              rows: rateOverrides.map((o) => ({
                  asset: `${prettify(o.assetType)} — ${sanitizeText(o.description)}`,
                  measure: o.measure === 'growth' ? 'Growth' : 'Income',
                  defaultPct: o.defaultPct,
                  chosenPct: o.chosenPct,
              })),
              chosen: describeRun(projections),
              atDefaults: describeRun(selectedStrategy.atDefaultRatesProjections),
          }
        : null;

    const calcCosts = await calculationApi.calculateAgedCareCosts({
        factFind: safeFactFind,
        financials: baseline,
        radInput: roomCost,
        radPaid: totalRadPaid,
        overallStrategy: {
            rentHome: Boolean(selectedStrategy?.scenarioConfig?.rentHome || selectedStrategy?.snapshot?.homeStatus === 'Rent Home'),
            downsizeHome: Boolean(selectedStrategy?.scenarioConfig?.downsizeHome || selectedStrategy?.snapshot?.homeStatus === 'Downsize'),
            // So the fee table prices the rules the strategy actually ran on (an opted-in run must not show the 2014 fee).
            residentialFeeArrangements,
        },
        extraServiceFeeInput: toNumber(agedCareCosts?.extraServiceFeeInput || 0),
    });
    const feeArrangementsComparison = coveredByNoWorseOff
        ? {
              arrangements: residentialFeeArrangements,
              personName: sanitizeText(enteringCarePersonName || `${factFind?.client?.firstName || ''} ${factFind?.client?.lastName || ''}`.trim() || 'the client'),
              chosen: describeRun(projections),
              optedIn: describeRun(selectedStrategy.optInProjections),
          }
        : null;

    const structureAppendix = buildDigitalSoaStructureAppendix(safeFactFind, {
        selectedStrategy,
        strategySource,
    });
    const structureAppendixHtml = buildDigitalSoaStructureAppendixHtml(
        structureAppendix,
        formatCurrency
    );

    return {
        reportId,
        generatedAt,
        adviceAttributionLine: attributionLine,
        adviceAttributionIncomplete: attributionIncomplete,
        audience: reportAudience,
        clientName: `${factFind?.client?.firstName || 'Client'} ${factFind?.client?.lastName || ''}`.trim(),
        partnerName: factFind?.partner?.firstName && factFind?.partner?.lastName ? `${factFind.partner.firstName} ${factFind.partner.lastName}` : '',
        completerDisplay:
            factFind?.completer && factFind.completer !== 'client'
                ? `${factFind.completerFirstName || ''} ${factFind.completerLastName || ''}`.trim() || sanitizeText(factFind.completer)
                : '',
        completerRelationship: sanitizeText(factFind?.completerRelationship || ''),
        completerIsApplicant,
        executiveSummaryOpeningHtml,
        careRecipientProfile,
        partnerContextHtml,
        horizonSetAsideHtml,
        horizonSetAsideNote,
        radFundingPrelude,
        homeRuleParagraph,
        wideExplorationClause,
        selectedStrategyName: sanitizeText(formatRadStrategyDisplayName(selectedStrategy.name, totalRadPaid, roomCost)),
        selectedStrategyNarrative: recommendedNarrative,
        totalScenarios: analysis?.totalScenarios || allScenarios.length || 0,
        roomCost,
        radPaid: totalRadPaid,
        estimatedDapAnnual: remainingForDap * mpir,
        /** The second room, where both of a couple enter care (7 Oct 2026). */
        /** Leaving a retirement village (8 Oct 2026): what the village pays, when, and what of it goes to the room. */
        villageExit: (() => {
            const row = (selectedStrategy as { villageExit?: { leaveDate?: string; refundDate?: string; entitlement?: number; fromVillage?: number; fromSavings?: number; toBank?: number; interim?: { days: number; perDay: number; total: number } | null; feesAfterLeaving?: { total: number; fullDays: number; sharedDays: number } | null; paidAtMove?: boolean } | null }).villageExit;
            const manual = (selectedStrategy.inputs as { villageExit?: { person?: string; leaveDate?: string; refundDate?: string; refundMonths?: number; entitlement?: number; fromVillage?: number; toBank?: number; interim?: { days: number; perDay: number; total: number } | null; feesAfterLeaving?: { total: number; fullDays: number; sharedDays: number } | null; paidAtMove?: boolean } | null } | undefined)?.villageExit;
            const optPerson = (selectedStrategy.strategyOptions as { villageExit?: { person?: string } } | undefined)?.villageExit?.person;
            const person = (manual?.person ?? optPerson) === 'partner' ? 'partner' : 'client';
            const name = sanitizeText((person === 'partner' ? safeFactFind.partner?.firstName : safeFactFind.client?.firstName) || 'the client');
            const refundMonths = (leave: string | undefined, refund: string | undefined) => (leave && refund ? Math.max(0, Math.round((new Date(refund).getTime() - new Date(leave).getTime()) / (30.44 * 24 * 60 * 60 * 1000))) : 0);
            const roomPrice = Number(selectedStrategy.radRoomPrice) || Number((selectedStrategy.inputs as { radInput?: number } | undefined)?.radInput) || 0;
            if (row && row.refundDate) {
                return villagePiece({ name, leaveLabel: row.leaveDate ? phaseFromLabel(row.leaveDate) : 'the coming weeks', entitlement: Number(row.entitlement) || 0, refundMonths: refundMonths(row.leaveDate, row.refundDate), refundDate: row.refundDate, roomPrice, deposit: totalRadPaid, fromVillage: Number(row.fromVillage) || 0, fromSavings: Number(row.fromSavings) || 0, soldPhrase: fundingPhrase(describeFundingKeys(safeFactFind, selectedStrategy.radFundingManualKeys as Record<string, number> | undefined), formatCurrency), toBank: Number(row.toBank) || 0, interim: row.interim ?? null, feesAfterLeaving: row.feesAfterLeaving ?? null, paidAtMove: !!row.paidAtMove, dapPerDay: Number((selectedStrategy.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number } | undefined)?.dailyAccommodationPayment) || 0 });
            }
            if (manual && manual.refundDate) {
                const fromVillage = Number(manual.fromVillage) || 0;
                return villagePiece({ name, leaveLabel: manual.leaveDate ? phaseFromLabel(manual.leaveDate) : 'the coming weeks', entitlement: Number(manual.entitlement) || 0, refundMonths: Number(manual.refundMonths) || refundMonths(manual.leaveDate, manual.refundDate), refundDate: manual.refundDate, roomPrice, deposit: totalRadPaid, fromVillage, fromSavings: Math.max(0, totalRadPaid - fromVillage), soldPhrase: fundingPhrase(describeFundingKeys(safeFactFind, selectedStrategy.inputs?.radFunding as Record<string, number> | undefined), formatCurrency), toBank: Number(manual.toBank) || 0, interim: manual.interim ?? null, feesAfterLeaving: manual.feesAfterLeaving ?? null, paidAtMove: !!manual.paidAtMove, dapPerDay: (Number(selectedStrategy.outputs?.estimatedDapAnnual) || 0) / 365 || Number((selectedStrategy.inputs as { estimatedDapDaily?: number } | undefined)?.estimatedDapDaily) || 0 });
            }
            return null;
        })(),
        /** A change of home (8 Oct 2026): what the plan does with it, in one paragraph — or that it stays put. */
        facilityMove: (() => {
            const row = (selectedStrategy as { facilityMove?: { stayPut?: boolean; moveDate?: string; newRoomPrice?: number; currentRoomPrice?: number; refundToday?: number; fromRefund?: number; fromSale?: number; grandfathered?: boolean } | null }).facilityMove;
            const manual = (selectedStrategy.inputs as { facilityMove?: { person?: string; moveDate?: string; facilityName?: string; currentRoomPrice?: number; paid?: number; refundToday?: number; retained?: number; retentionDaysTaken?: number; grandfathered?: boolean; newRoomPrice?: number; radTarget?: number } | null } | undefined)?.facilityMove;
            const person = manual?.person === 'partner' ? 'partner' : 'client';
            const name = sanitizeText((person === 'partner' ? safeFactFind.partner?.firstName : safeFactFind.client?.firstName) || 'the client');
            const stayRecord = person === 'partner' ? safeFactFind.partner?.careStay : safeFactFind.client?.careStay;
            const facilityName = sanitizeText(String(manual?.facilityName || stayRecord?.facilityName || ''));
            if (row) {
                if (row.stayPut) return `${name} stays at ${facilityName || 'the current home'}: the deposit held stays where it is and nothing is sold.`;
                const deposit = (Number(row.fromRefund) || 0) + (Number(row.fromSale) || 0);
                return movePiece({ name, facilityName, moveLabel: row.moveDate ? phaseFromLabel(row.moveDate) : 'the coming weeks', newRoomPrice: Number(row.newRoomPrice) || 0, deposit, fromRefund: Number(row.fromRefund) || 0, fromSale: Number(row.fromSale) || 0, soldPhrase: fundingPhrase(describeFundingKeys(safeFactFind, selectedStrategy.radFundingManualKeys as Record<string, number> | undefined), formatCurrency), dapPerDay: Number((selectedStrategy.strategySpecificAgedCareCosts as { dailyAccommodationPayment?: number } | undefined)?.dailyAccommodationPayment) || 0, grandfathered: !!row.grandfathered, retentionDaysTaken: 0 });
            }
            if (manual && (Number(manual.newRoomPrice) || 0) > 0) {
                const deposit = Math.min(Number(manual.newRoomPrice) || 0, Number(manual.radTarget) || 0);
                const fromRefund = Math.min(Number(manual.refundToday) || 0, deposit);
                return movePiece({ name, facilityName, moveLabel: manual.moveDate ? phaseFromLabel(manual.moveDate) : 'the coming weeks', newRoomPrice: Number(manual.newRoomPrice) || 0, deposit, fromRefund, fromSale: Math.max(0, deposit - fromRefund), soldPhrase: fundingPhrase(describeFundingKeys(safeFactFind, selectedStrategy.inputs?.radFunding as Record<string, number> | undefined), formatCurrency), dapPerDay: (Number(selectedStrategy.outputs?.estimatedDapAnnual) || 0) / 365 || Number((selectedStrategy.inputs as { estimatedDapDaily?: number } | undefined)?.estimatedDapDaily) || 0, grandfathered: !!manual.grandfathered, retentionDaysTaken: Number(manual.retentionDaysTaken) || 0 });
            }
            return null;
        })(),
        secondRoom: (() => {
            const sr = (selectedStrategy as { secondRoom?: { person?: string; roomPrice?: number; radPaid?: number; agedCareCosts?: { totalDailyCost?: number } } }).secondRoom
                ?? (selectedStrategy.inputs?.secondRoom && selectedStrategy.inputs.secondRoom.roomPrice > 0
                    ? { person: (selectedStrategy.inputs.secondRoom as { person?: string }).person ?? 'partner', roomPrice: selectedStrategy.inputs.secondRoom.roomPrice, radPaid: selectedStrategy.inputs.secondRoom.radPaid, agedCareCosts: undefined }
                    : null);
            if (!sr || !(Number(sr.roomPrice) > 0)) return null;
            const names = roleNames(safeFactFind, sr.person === 'client' ? 'client' : 'partner', { first: 'the first to move in', second: 'the partner' });
            const name = names.secondName;
            // The second stage, where the second person enters later: an engine row carries it as
            // `phaseTwo`; a manual plan in its saved outputs.
            const p2 = ((selectedStrategy as { phaseTwo?: { fromDate?: string; householdDailyCost?: number } | null }).phaseTwo
                ?? (selectedStrategy.outputs as { phaseTwo?: { fromDate?: string; householdDailyCost?: number } | null } | undefined)?.phaseTwo) || null;
            const firstDaily = Number((selectedStrategy.strategySpecificAgedCareCosts as { totalDailyCost?: number } | undefined)?.totalDailyCost)
                || (Number(selectedStrategy.outputs?.agedCareCost) || 0) / 365;
            const phaseLine = p2 && p2.fromDate && typeof p2.householdDailyCost === 'number'
                ? recapPhaseLine({ firstName: sanitizeText(names.firstName), secondName: sanitizeText(name), fromLabel: phaseFromLabel(p2.fromDate) }, firstDaily, p2.householdDailyCost)
                : null;
            return { name: sanitizeText(name), roomPrice: Number(sr.roomPrice) || 0, radPaid: Number(sr.radPaid) || 0, dailyTotal: sr.agedCareCosts?.totalDailyCost ?? null, phaseLine };
        })(),
        annualPension: selectedStrategy.snapshot?.pension || 0,
        annualCashflow,
        projectedLegacy: selectedStrategy.snapshot?.legacyValue || selectedStrategy.outputs?.legacyEstimate || Number(selectedStrategy.projectedEstateValue) || 0,
        baseline: {
            netWorth: baseline.netWorth || 0,
            cashflow: baseline.cashFlow || 0,
            pension: baselineAnnualPension
        },
        strategyOpeningNetWorth,
        openingNetWorthTipRows,
        illnessSeparatedAppliesInSoa,
        illnessSeparatedDiffersFromBaseline,
        baselineUsesIllnessSeparated,
        modelUsesIllnessSeparated,
        baselinePensionStatus,
        selectedPensionStatus,
        pensionDeltaFromBaseline,
        enteringCarePersonName,
        fees: {
            basicDailyFee: toNumber(calcCosts?.basicDailyFee),
            dailyNCCC: toNumber(calcCosts?.dailyNCCC),
            dailyHSC: toNumber(calcCosts?.dailyHSC),
            dailyMTCF: toNumber(calcCosts?.dailyMTCF),
            feeArrangements: calcCosts?.feeArrangements === '2014' ? ('2014' as const) : ('2025' as const),
            dailyExtraServiceFee: toNumber(calcCosts?.dailyExtraServiceFee)
        },
        feeArrangementsComparison,
        recommendations: (analysis?.recommendedOptimalStrategies || []).map((row) => ({
            label: sanitizeText(row.label),
            name: sanitizeText(stripFundingSuffix(row.strategy?.name || 'Recommended strategy'))
        })),
        optimalStrategyCardsPrimary: displayOptimalCards,
        optimalStrategyCardsConditional: conditionalOptimalCards,
        hasConditionalStrategyCards: conditionalOptimalCards.length > 0,
        /** @deprecated Combined list for callers that expect a single array; prefer primary + conditional. */
        optimalStrategyCards: [...displayOptimalCards, ...conditionalOptimalCards],
        strategySource,
        scenarioExplainer,
        statedGoals,
        inScopeGoals,
        outOfScopeGoals,
        pathComparison,
        opportunityCost,
        cashflowIsNegative,
        deficitFundingNarrative,
        fundingRunway,
        projectionSurplusDeficitNote,
        cashflowAnalysisSummary: cashflowAnalysis
            ? {
                  year1Cashflow: cashflowAnalysis.year1Cashflow,
                  firstDeficitYear: cashflowAnalysis.firstDeficitYear,
                  firstDeficitAmount: cashflowAnalysis.firstDeficitAmount,
                  hasDeficitYears: cashflowAnalysis.hasDeficitYears,
                  isYear1Surplus: cashflowAnalysis.isYear1Surplus
              }
            : null,
        projectionPeriodMode: rowPeriodMode,
        careStartDate,
        radFundingDate,
        radDrawdown,
        enrichedProjections,
        topThree,
        radFundingRows,
        adviceImplications,
        pensionNextStep,
        // The same "What could go wrong" list the card shows (strategyRisks.ts), so the document
        // and the screen cannot disagree about the risks of the chosen strategy.
        strategyRisks: buildStrategyRisks(riskKindsFromAgedCareStrategy(selectedStrategy as never), {
            horizonWarning: engineStrategyHorizonWarning(selectedStrategy as never, factFind as never),
        }) as StrategyRisks | undefined,
        projections,
        assetRows,
        // The shared document pieces (features/advice/soaKit.ts, 3 Oct 2026): the position as
        // entered, the do-nothing run's year-by-year for the charts, and the Assumptions page as
        // it stood when the document was generated, for the appendix.
        situation: situationFrom(factFind as never),
        legacyHorizonYears,
        doNothingLegacy: doNothing ? toNumber(doNothingSnap.legacyValue) : null,
        doNothingCashflow: doNothing ? toNumber(doNothingSnap.netCashflow) : null,
        stayPutLegacy,
        baselineProjections: ((doNothing?.projections || []) as SoaProjectionYear[]).map((p, i) => ({
            label: sanitizeText(String(p.fyLabel || `Year ${p.year ?? i + 1}`)),
            pension: toNumber(p.socialSecurity ?? p.pension),
            cashflow: toNumber(p.cashFlow ?? p.cashflow),
            netWorth: toNumber(p.netWorth),
        })),
        assumptionSections: buildAssumptionSections(),
        assumptionsChanged,
        allFactFindRows: flattenObject(factFind || {}),
        familyEstate,
        estateRisk:
            familyEstate.clientWill === 'no' ||
            familyEstate.partnerWill === 'no' ||
            familyEstate.clientEpoa === 'no' ||
            familyEstate.partnerEpoa === 'no' ||
            factFind?.goals?.hasWill === false ||
            String(factFind?.goals?.estatePlanning || '').toLowerCase() === 'no',
        annualLivingExpenses: toNumber(factFind?.expenses?.livingExpenses),
        adjustedLivingExpenses: toNumber(scenarioLivingExpensesOr(agedCareCosts?.scenarioLivingExpenses, factFind?.expenses?.livingExpenses)),
        clientAge: calculateAge(factFind?.client?.dob) ?? 'N/A',
        partnerAge: factFind?.partner?.dob ? (calculateAge(factFind.partner.dob) ?? 'N/A') : 'N/A',
        isCoupleAdvice,
        factFindSections: buildFactFindSections(factFind || {}),
        structureAppendix,
        structureAppendixHtml,
    };
}

/**
 * The full digital-SOA snapshot handed to buildHtml() and its helpers below — derived straight from
 * buildAgedCareDigitalSOASnapshot's own (very large) return object rather than hand-duplicated, so this
 * stays exact as that function evolves.
 */
type SoaSnapshot = NonNullable<Awaited<ReturnType<typeof buildAgedCareDigitalSOASnapshot>>>;

/**
 * The "Assumptions you changed" box — nothing where the client left every rate alone.
 *
 * Not a footnote. A rate the client raised is the one input on the document that is theirs rather
 * than ours, so it gets its own table, a sentence in plain words saying whose decision it was, and
 * the consequence in dollars from the same plan run at our rates. The comparison is only offered
 * where the engine actually produced that run; an older saved strategy without one is not guessed at.
 */
function buildAssumptionsChangedHtml(snapshot: SoaSnapshot): string {
    const changed = snapshot.assumptionsChanged;
    if (!changed) return '';
    const pct = (v: number) => `${v.toFixed(2)}%`;
    const rows = changed.rows
        .map((r) => `<tr><td>${r.asset}</td><td>${r.measure}</td><td>${pct(r.defaultPct)}</td><td><strong>${pct(r.chosenPct)}</strong></td><td>${r.chosenPct > r.defaultPct ? 'Higher' : 'Lower'}</td></tr>`)
        .join('');
    const comparison = changed.chosen && changed.atDefaults
        ? `<p><strong>What the difference is worth.</strong> This plan, at the rates ${changed.clientName} chose: savings last ${changed.chosen.savingsLast}, and the estimated estate at the end of the ${changed.chosen.years}-year plan is <strong>${formatCurrency(changed.chosen.estate)}</strong>. The same plan — the same assets sold, in the same order, the same deposit and the same borrowing — at AgedCareWise's default rates: savings last ${changed.atDefaults.savingsLast}, and the estimated estate is <strong>${formatCurrency(changed.atDefaults.estate)}</strong>.</p>`
        : `<p class="small muted">A comparison of this plan at AgedCareWise's default rates was not available for this document.</p>`;
    return `
<div class="h3">Assumptions you changed</div>
<table><thead><tr><th>Asset</th><th>Rate</th><th>AgedCareWise default</th><th>Rate chosen</th><th></th></tr></thead><tbody>${rows}</tbody></table>
<p>These rates were chosen by <strong>${changed.clientName}</strong>. They are ${changed.anyAbove ? 'higher than' : 'different from'} the rates AgedCareWise uses by default and are <strong>not our recommendation</strong>. The projections in this document run on them.</p>
${changed.anyAbove ? `<p><strong>The risk.</strong> A projection is only as reliable as the assumptions behind it. Higher returns are not free: an asset expected to earn more is usually one whose returns swing more from year to year, and a run of poor years early in aged care — when fees are being paid every day — cannot be made up later. If the returns turn out lower than assumed, savings run out sooner and the estate is smaller than shown here.</p>` : ''}
${comparison}`;
}

/** The model's Comparison Yr1 pension lines — the evidence behind the Age Pension section, couple or single. */
function pensionBreakdownDetailsHtml(snapshot: SoaSnapshot): string {
    return `<details>
<summary><strong>Breakdown (Comparison Yr1 projection only)</strong> — Evidence of how the model calculated Age Pension</summary>
<p class="small muted">The lines below are the model's Comparison Yr1 social security calculation breakdown for this selected strategy.</p>
<table>
<thead><tr><th>Calculation line</th><th>Comparison Yr1 value</th></tr></thead>
<tbody>
${(snapshot.enrichedProjections?.[0]?.pensionBreakdown || []).length > 0
    ? snapshot.enrichedProjections[0].pensionBreakdown.map((row) => {
        const label = sanitizeText(String(row?.label || 'Line'));
        const value = sanitizeText(String(row?.value ?? ''));
        const isSeparator = label.startsWith('---');
        if (isSeparator) {
            return `<tr><td colspan="2"><strong>${label.replace(/---/g, '').trim() || 'Section'}</strong></td></tr>`;
        }
        return `<tr><td>${label}</td><td>${value || '—'}</td></tr>`;
    }).join('')
    : '<tr><td colspan="2">Comparison Yr1 pension breakdown was not attached in this export payload.</td></tr>'}
</tbody>
</table>
</details>`;
}

function formatDeltaCurrency(delta: unknown): string {
    const n = Number(delta) || 0;
    const abs = formatCurrency(Math.abs(n));
    if (n > 0) return `+${abs}`;
    if (n < 0) return `−${abs}`;
    return abs;
}

/**
 * Opening net worth tooltip lines — matches {@link ProjectionsModal} `createAssetBreakdown`:
 * prefer year-1 projection row assets/home/liabilities; fall back to fact-find asset listing.
 */
function buildOpeningNetWorthBreakdownLines(
    projectionRow: SoaProjectionYear | null | undefined,
    openingNetWorth: number,
    fallbackFactFind: DraftFactFind | null | undefined,
): SoaBreakdownItem[] {
    const lines: SoaBreakdownItem[] = [];
    const row = projectionRow;
    const pushTotal = () => {
        lines.push({ label: '=== TOTAL NET WORTH ===', value: formatCurrency(openingNetWorth) });
    };

    if (row && (row.assets || row.home)) {
        if (row.home && parseFloat(String(row.home.value)) > 0) {
            lines.push({
                label: sanitizeText(row.home.description || 'Family Home'),
                value: formatCurrency(parseFloat(String(row.home.value)) || 0),
            });
        }
        if (row.assets && typeof row.assets === 'object') {
            Object.entries(row.assets).forEach(([assetType, assetList]) => {
                if (!Array.isArray(assetList)) return;
                assetList.forEach((asset) => {
                    let v = parseFloat(String(asset.value)) || 0;
                    if (asset.type === 'annuities' && asset.residualValue != null && asset.residualValue !== '') {
                        v = parseFloat(String(asset.residualValue)) || 0;
                    }
                    if (v > 0) {
                        lines.push({
                            label: sanitizeText(asset.description || asset.name || `${prettify(assetType)} asset`),
                            value: formatCurrency(v),
                        });
                    }
                });
            });
        }
        let totalLiabilities = 0;
        Object.values(row.liabilities || {}).forEach((list) => {
            (list || []).forEach((l) => {
                totalLiabilities += parseFloat(String(l.balance)) || 0;
            });
        });
        if (totalLiabilities > 0) {
            lines.push({ label: 'Less: Liabilities', value: `-${formatCurrency(totalLiabilities)}` });
        }
        if (lines.length > 0) {
            pushTotal();
            return lines;
        }
    }

    const fd = fallbackFactFind || ({} as DraftFactFind);
    if (fd.home?.value && parseFloat(String(fd.home.value)) > 0) {
        lines.push({ label: 'Family Home', value: formatCurrency(parseFloat(String(fd.home.value)) || 0) });
    }
    ASSET_TYPES.forEach((type) => {
        const assetList = fd.assets?.[type.id];
        if (!type.isLiquid || !assetList) return;
        assetList.forEach((asset, index) => {
            const rawValue = asset.balance != null ? asset.balance : asset.value;
            const numeric = rawValue || 0;
            if (numeric > 0) {
                lines.push({
                    label: sanitizeText(`${(asset as { name?: string }).name || asset.description || type.name} ${index + 1}`),
                    value: formatCurrency(numeric),
                });
            }
        });
    });
    const radPaidAssets = fd.assets?.radPaid;
    if (radPaidAssets && radPaidAssets.length > 0) {
        radPaidAssets.forEach((rad, index) => {
            const val = parseFloat(String(rad.value)) || 0;
            if (val > 0) lines.push({ label: `RAD Paid ${index + 1}`, value: formatCurrency(val) });
        });
    }
    if (lines.length > 0) {
        pushTotal();
        return lines;
    }
    return [{ label: 'Total net worth (opening)', value: formatCurrency(openingNetWorth) }];
}

/**
 * Care entry and the funding sale, as dated by THIS strategy.
 *
 * They are separate acts and the engine models the gap between them — proceeds sit in the bank earning
 * deemed income until the deposit is paid. Saying a sale happens "on the entry date" under a strategy
 * that sells three months earlier misdescribes the client's own advice, and hides the months where the
 * money is assessable.
 */
function careAndSaleTimingSentence(snapshot: SoaSnapshot): string {
    return careTimingSentence(snapshot);
}

/**
 * The provider agreement the drawdown rows rest on. Stated because it is a fact the client has to
 * confirm with the home, not something the model can know: the accommodation payment may always be
 * drawn from the deposit, the other fees only where the provider has agreed in writing.
 */
function depositArrangementSentence(snapshot: SoaSnapshot): string {
    switch (snapshot.radDrawdown) {
        case 'none':
            return 'The arrangement modelled does not allow any fees to be drawn from the refundable accommodation deposit; once savings run low the plan borrows against the home instead.';
        case 'dap':
            return 'Under the arrangement modelled only the daily accommodation payment may be drawn from the refundable accommodation deposit; other fees are met from savings, then by borrowing against the home.';
        default:
            return 'Under the arrangement modelled, care fees may be drawn from the refundable accommodation deposit once savings run low — the daily accommodation payment as of right, the other fees by written agreement with the provider. Each dollar drawn reduces the refund at the end of care and increases the daily accommodation payment.';
    }
}

/**
 * Which fee rules the resident is on, and what the other choice would be worth — only for a resident
 * the profile puts under the no-worse-off principle. The election to opt in cannot be undone, so
 * the document states both the rules modelled and the whole-of-horizon comparison the engine ran.
 */
function feeArrangementsParagraph(snapshot: SoaSnapshot): string {
    const c = snapshot.feeArrangementsComparison;
    if (!c) return '';
    const who = c.personName;
    if (c.arrangements === '2025') {
        return `<p><strong>Fee arrangements.</strong> Because a Home Care Package was in place or approved for ${who} on 12 September 2024, the 'no worse off' principle would put ${who} on the 1 July 2014 fee arrangements (a means-tested care fee) on entering care. <strong>This plan is modelled as opting in to the 1 November 2025 fee arrangements</strong> — the hotelling and non-clinical care contributions. Opting in is an election made to the provider and <strong>cannot be reversed</strong>; obtain advice before making it.</p>`;
    }
    const comparison = c.chosen && c.optedIn
        ? ` <strong>What opting in would be worth:</strong> on these rules, savings last ${c.chosen.savingsLast} and the estimated estate at the end of the ${c.chosen.years}-year plan is <strong>${formatCurrency(c.chosen.estate)}</strong>; the same plan opted in to the 1 November 2025 rules gives savings lasting ${c.optedIn.savingsLast} and an estate of <strong>${formatCurrency(c.optedIn.estate)}</strong> — ${c.optedIn.estate >= c.chosen.estate ? `${formatCurrency(c.optedIn.estate - c.chosen.estate)} more` : `${formatCurrency(c.chosen.estate - c.optedIn.estate)} less`}.`
        : '';
    return `<p><strong>Fee arrangements.</strong> Because a Home Care Package was in place or approved for ${who} on 12 September 2024, the 'no worse off' principle puts ${who} on the <strong>1 July 2014 fee arrangements</strong>: a means-tested care fee (capped each year of care and over a lifetime, with fees already paid under the package counting towards the lifetime cap) instead of the hotelling and non-clinical care contributions. Accommodation is on the 1 November 2025 arrangements either way, so deposit retention and DAP indexation still apply. ${who} may opt in to the 1 November 2025 fee arrangements at any time, but the election cannot be reversed.${comparison}</p>`;
}

function careTimingSentence(snapshot: SoaSnapshot): string {
    const careStartOn = parseIsoDate(snapshot.careStartDate);
    const radFundingOn = parseIsoDate(snapshot.radFundingDate);
    if (!careStartOn) {
        return 'Where a future care start date is set, care fees and deposit funding are modelled from that date — nothing is charged before entry.';
    }
    const sellsBeforeEntry = radFundingOn != null && radFundingOn < careStartOn;
    return `Residential care is modelled as beginning on ${formatLongDate(careStartOn)}; fees are charged from that date and nothing is charged before entry.`
        + (sellsBeforeEntry
            ? ` Assets nominated to fund the accommodation deposit are sold earlier, on ${formatLongDate(radFundingOn!)}, and the proceeds are held in the bank — deemed and means-tested — until the deposit is paid.`
            : ' Assets nominated to fund the accommodation deposit are sold on that same date.');
}

function buildProjectionTableBodyFromSnapshot(snapshot: SoaSnapshot): string {
    const rows = snapshot.enrichedProjections || [];
    if (rows.length === 0) {
        return '<tr><td colspan="7">No year-by-year projections were attached to this strategy export.</td></tr>';
    }
    const tipBubble = (title: string, rowsForTip: SoaBreakdownItem[] | null | undefined, emptyText = 'Breakdown not available for this year.') => {
        const items = (rowsForTip || []).filter((r) => r && (r.label || r.value !== undefined));
        const content = items.length
            ? `<div class="tip-grid">${items
                  .map((r) => {
                      const label = sanitizeText(r.label || 'Line');
                      if (label.startsWith('---')) return '<div style="border-top:1px solid rgba(148,163,184,.35);margin:5px 0"></div>';
                      const raw = r.value;
                      const n = parseMoneyish(raw);
                      const hasMoneySign = typeof raw === 'string' && raw.includes('$');
                      const isPercent = typeof raw === 'string' && raw.includes('%');
                      const displayVal =
                          isPercent
                              ? sanitizeText(String(raw))
                              : (hasMoneySign || Math.abs(n) > 0)
                              ? `${n >= 0 ? '+' : '-'}${formatCurrency(Math.abs(n))}`
                              : sanitizeText(String(raw ?? ''));
                      return `<div class="tip-row"><span class="tip-lbl">${label}:</span><span class="tip-val">${displayVal}</span></div>`;
                  })
                  .join('')}</div>`
            : `<div class="small muted">${emptyText}</div>`;
        return `<span class="cc-tip-wrap">
<button type="button" class="cc-tip-trigger" aria-label="${sanitizeText(title)}">i</button>
<span class="cc-tip" role="tooltip"><strong>${sanitizeText(title)}</strong>${content}</span>
</span>`;
    };

    const withTip = (valueHtml: string, title: string, rowsForTip: SoaBreakdownItem[] | null | undefined, emptyText?: string) => {
        return `<span class="metric-cell">${valueHtml}${tipBubble(title, rowsForTip, emptyText)}</span>`;
    };

    const openingNw = Number(snapshot.strategyOpeningNetWorth ?? snapshot.baseline?.netWorth ?? 0);
    const openingTipRows =
        snapshot.openingNetWorthTipRows?.length > 0
            ? snapshot.openingNetWorthTipRows
            : buildOpeningNetWorthBreakdownLines(snapshot.projections?.[0], openingNw, {} as DraftFactFind);

    // Wording follows the calendar the rows were actually cut on — see the snapshot's own note.
    const period = describeProjectionPeriod(snapshot.projectionPeriodMode);


    const fyBlendExtras: string[] = [];
    const row0 = rows[0];
    // Only meaningful under financial-year rows: the blend day-weights across 1 July–30 June
    // (computeYearOneFullAustralianFyBlend), so beneath calendar rows it would describe a period this
    // table never shows. Suppressed rather than relabelled — the figures really are FY figures.
    const fy = period.differsFromTaxYear ? undefined : row0?.fyFullYearView;
    const b = fy?.blended;
    if (fy && b) {
        const basePrePension = fy.baselineProjectionRow?.socialSecurity ?? 0;
        const basePostPension = fy.strategyProjectionRow?.socialSecurity ?? 0;
        const basePreCare = fy.baselineProjectionRow?.annualCareCost ?? 0;
        const basePostCare = fy.strategyProjectionRow?.annualCareCost ?? 0;
        const fyOpening = fy.netWorthOpening ?? openingNw;
        const stratClosing = Number(row0?.netWorth ?? 0);

        fyBlendExtras.push(`<tr class="proj-tr-fy-wrap"><td colspan="7" class="proj-fy-cell">
<details class="soa-fy-details">
<summary class="soa-fy-summary">Show full <strong>${sanitizeText(fy.fyLabel || 'financial year')}</strong> row <span class="soa-fy-est">(estimate)</span></summary>
<div class="soa-fy-inner-wrap">
<p class="small muted soa-fy-lede">Separate estimate: combines before- and after-strategy positions within the financial year (saved profile from 1 July to the day before the strategy start, then your modelled strategy to 30 June; day-weighted). Illustrative only.</p>
<table class="table-proj table-proj-fy">
<tbody><tr class="proj-tr-fy-data">
<td class="proj-year"><span class="block">${sanitizeText(fy.fyLabel || '')} (estimate)</span><span class="small muted block">Full financial year blend</span></td>
<td class="td-num">${withTip(formatCurrency(fyOpening), 'Opening net worth (FY estimate)', [
            { label: 'Current file (fact-find)', value: formatCurrency(fyOpening) },
            { label: 'Note', value: 'Comparison Yr1 uses your strategy-path opening when that differs from your saved profile.' },
        ])}</td>
<td class="td-num age-pos">${withTip(`${(b.socialSecurity ?? 0) >= 0 ? '+' : ''}${formatCurrency(b.socialSecurity ?? 0)}`, 'Age Pension (FY estimate)', [
            { label: 'Blended annual rate (day-weighted)', value: formatCurrency(b.socialSecurity ?? 0) },
            { label: 'Pre-strategy (p.a.)', value: formatCurrency(basePrePension) },
            { label: 'Post-strategy (p.a.)', value: formatCurrency(basePostPension) },
        ])}</td>
<td class="td-num age-neg">${withTip(`-${formatCurrency(b.annualCareCost ?? 0)}`, 'Aged care costs (FY estimate)', [
            { label: 'Blended annual cost (day-weighted)', value: formatCurrency(b.annualCareCost ?? 0) },
            { label: 'Pre-strategy (p.a.)', value: formatCurrency(basePreCare) },
            { label: 'Post-strategy (p.a.)', value: formatCurrency(basePostCare) },
            {
                label: 'Pre-strategy vs post-strategy',
                value: 'Pre-strategy uses your saved profile before residential aged-care optimisation. Post-strategy matches Comparison Yr1.',
            },
        ])}</td>
<td class="td-num ${(b.cashFlow ?? 0) >= 0 ? 'age-pos' : 'age-neg'}">${withTip(`${(b.cashFlow ?? 0) >= 0 ? '+' : ''}${formatCurrency(b.cashFlow ?? 0)}`, 'Net cashflow (FY estimate)', [
            { label: 'Blended net cashflow after care (p.a. equivalent)', value: formatCurrency(b.cashFlow ?? 0) },
            { label: 'Tax component (net)', value: formatCurrency(b.tax ?? 0) },
            { label: 'Living expenses (blend)', value: formatCurrency(b.livingExpenses ?? 0) },
        ])}</td>
<td class="td-num">${withTip('—', 'Closing net worth (full FY row)', [
            { label: 'Why no figure here', value: 'This row only shows day-blended pension, care costs and cashflow for the full financial year. Closing net worth is not rebuilt on that blended basis.' },
            { label: 'Strategy-path closing (Comparison Yr1)', value: formatCurrency(stratClosing) },
        ])}</td>
<td class="td-num"><span class="metric-cell muted small">Illustrative blend</span></td>
</tr></tbody></table></div>
</details>
</td></tr>`);
    }

    const dataRows = rows
        .map((row, i) => {
            const netWorthStart =
                i === 0 ? openingNw : Number(rows[i - 1]?.netWorth ?? 0);
            const netWorthEnd = Number(row.netWorth ?? 0);
            const startBreakdown =
                i === 0 ? openingTipRows : rows[i - 1]?.netWorthBreakdown || [];
            const endBreakdown = row.netWorthBreakdown || [];

            const yearCol =
                row.fyLabel && i === 0
                    ? `<div class="proj-year-stack"><strong class="proj-cmp-label">Comparison Yr1</strong><span class="small muted">${sanitizeText(row.fyLabel)}${row.isPartialFy ? ` · partial ${period.mode === 'calendar_year' ? 'year' : 'FY'}` : ''}</span></div>`
                    : row.fyLabel
                      ? `<div class="proj-year-stack">${sanitizeText(row.fyLabel)}${row.isPartialFy ? `<span class="small muted"> (partial ${period.mode === 'calendar_year' ? 'year' : 'FY'})</span>` : ''}</div>`
                      : `Year ${row.year}`;

            const mainTr = `<tr class="proj-tr-data${i % 2 === 1 ? ' proj-zebra' : ''}">
<td class="proj-year">${yearCol}</td>
<td class="td-num">${withTip(formatCurrency(netWorthStart), 'Net Worth (Opening) Breakdown', startBreakdown)}</td>
<td class="td-num age-pos">${withTip(`+${formatCurrency(row.pension)}`, 'Age Pension Breakdown', row.pensionBreakdown)}</td>
<td class="td-num age-neg">${withTip(`-${formatCurrency(row.careAnnual)}`, 'Aged Care Costs Breakdown', row.careBreakdown)}</td>
<td class="td-num ${row.cashFlow >= 0 ? 'age-pos' : 'age-neg'}">${withTip(`${row.cashFlow >= 0 ? '+' : ''}${formatCurrency(row.cashFlow)}`, 'Net Cashflow Breakdown', row.cashBreakdown)}</td>
<td class="td-num">${withTip(formatCurrency(netWorthEnd), 'Net Worth (Closing) Breakdown', endBreakdown)}</td>
<td><span class="metric-cell">${buildSurplusDeficitText(row)}${tipBubble('Surplus / Deficit Detail', row.cashFlow < 0 ? row.drawdownBreakdown : [{ label: 'Treatment', value: 'Surplus retained and carried into next year balances.' }])}</span></td>
</tr>`;

            if (i === 0 && fyBlendExtras.length) {
                return `${mainTr}${fyBlendExtras.join('')}`;
            }
            return mainTr;
        })
        .join('');

    return dataRows;
}

interface SoaRadFundingRow {
    assetTypeKey: string;
    assetId?: unknown;
    assetType: string;
    source: string;
    amount: number;
}

interface SoaAdviceImplicationItem {
    title: string;
    lead: string;
    body: string;
    cgtLine?: string;
}

export function buildAdviceImplications({
    radFundingRows,
    radFundingCgtTotal,
    cgtAssumptionDisclosure,
    annuityTerms,
    clientFirstName,
    partnerFirstName
}: {
    radFundingRows: SoaRadFundingRow[];
    /** CGT the modelled RAD funding triggers, from `computeRadFundingTotals` — 0 when no gain is realised. */
    radFundingCgtTotal?: number;
    /** Set when an asset being sold had no recorded cost base and/or acquisition date. */
    cgtAssumptionDisclosure?: string | null;
    /**
     * The terms of an annuity this strategy buys, where it buys one.
     *
     * THE DOCUMENT OF RECORD SAID NOTHING ABOUT THEM. A strategy could commit $500,000 of a client's
     * money to a lifetime annuity and the Statement of Advice named neither the owner, nor whether
     * the capital came back, nor - for a couple - whether the income continued to the survivor. The
     * screens now disclose all of it; a client who selected the strategy and generated the SOA
     * without opening the explanation saw none of it (2026-09-01).
     */
    annuityTerms?: Record<string, unknown> | null;
    clientFirstName?: string;
    partnerFirstName?: string;
}): SoaAdviceImplicationItem[] {
    const grouped: Record<string, { amount: number; assets: string[] }> = {};
    (radFundingRows || []).forEach((row) => {
        const key = row.assetTypeKey || 'other';
        if (!grouped[key]) grouped[key] = { amount: 0, assets: [] };
        grouped[key].amount += toNumber(row.amount);
        grouped[key].assets.push(row.source);
    });

    const cgtTotal = toNumber(radFundingCgtTotal);
    const items: SoaAdviceImplicationItem[] = Object.entries(grouped).map(([assetTypeKey, data]) => {
        const template = ASSET_IMPLICATION_TEMPLATES[assetTypeKey] || ASSET_IMPLICATION_TEMPLATES.other!;
        const sourceList = Array.from(new Set(data.assets)).slice(0, 3).join(', ');
        const hasCgt = assetTypeKey === 'shares' && cgtTotal > 0;
        const cgtLine = hasCgt
            ? `Estimated CGT from modelled RAD funding: <strong>${formatCurrency(cgtTotal)}</strong>.`
            : '';
        return {
            title: `${prettify(assetTypeKey)} implications`,
            lead: `Modelled drawdown from this asset type: <strong>${formatCurrency(data.amount)}</strong>${sourceList ? ` (${sanitizeText(sourceList)})` : ''}.`,
            body: template,
            cgtLine
        };
    });

    // Pushed rather than returned early: a strategy can sell an investment property without any RAD
    // liquidation, and the CGT-assumption disclosure below still has to reach the document in that case.
    if (items.length === 0) {
        items.push({
            title: 'Implementation implications',
            lead: 'No upfront RAD liquidation was required in this strategy.',
            body: 'You should still validate product conditions, tax outcomes, and legal consequences before implementing any future drawdowns.'
        });
    }

    /*
     * THE ANNUITY, IN FULL. Placed before the generic product-provider note because it is specific
     * to what was recommended, and because the terms are the part a family reads this document to
     * find out. Written as prose rather than a table: an SOA is read, not scanned.
     */
    if (annuityTerms && toNumber(annuityTerms.purchaseAmount) > 0) {
        const purchase = toNumber(annuityTerms.purchaseAmount);
        const rcv = toNumber(annuityTerms.residualPercent);
        const ownerIsPartner = annuityTerms.owner === 'partner';
        const client = sanitizeText(clientFirstName || 'the applicant');
        const partner = sanitizeText(partnerFirstName || '');
        const owner = ownerIsPartner ? (partner || 'the partner') : client;
        const survivor = ownerIsPartner ? client : partner;
        const income = toNumber(annuityTerms.annualPayment);
        const reversionary = !!annuityTerms.reversionary;

        const scheduleYears = annuityTerms.capitalAccessPeriodYears != null ? toNumber(annuityTerms.capitalAccessPeriodYears) || null : null;
        const sexKnownForWords = annuityTerms.lifeExpectancySexKnown == null ? null : !!annuityTerms.lifeExpectancySexKnown;
        const capitalSentence = rcv >= 99.5
            ? `${sanitizeText(capitalReturnedWords({ residualPct: 100, periodYears: scheduleYears, sexKnown: sexKnownForWords, ownerName: owner, amountWords: `the purchase price of ${formatCurrency(purchase)}` }))} This is why the income is lower than a nil-residual annuity would pay.`
            : rcv <= 0.5
                ? `<strong>Nothing is returned</strong> at the end — the provider keeps the ${formatCurrency(purchase)}. This is why the income is higher, and it means the annuity leaves nothing to the estate.`
                : sanitizeText(capitalReturnedWords({ residualPct: rcv, periodYears: scheduleYears, sexKnown: sexKnownForWords, ownerName: owner, amountWords: `the ${formatCurrency(purchase)}` }));

        const survivorSentence = survivor
            ? (reversionary
                ? `The annuity is <strong>reversionary</strong>: if ${owner} dies first, the income continues to ${survivor} for the rest of their life.`
                : `The annuity is <strong>not reversionary</strong>: the income stops on ${owner}'s death and ${survivor} does not continue receiving it. Whether any money returns is a separate question, answered above.`)
            : `The income stops on ${owner}'s death.`;

        items.push({
            title: 'Annuity terms',
            lead: `Modelled purchase: <strong>${formatCurrency(purchase)}</strong> into a lifetime annuity owned by <strong>${owner}</strong>${income > 0 ? `, paying approximately <strong>${formatCurrency(income)}</strong> a year` : ''}.`,
            body: `${capitalSentence} ${survivorSentence} The payment is <strong>not indexed</strong> — it does not rise with inflation, so its buying power falls over time. `
                + `Centrelink counts 60% of the purchase price in the assets test for the first five years, or until age 85 if that is later, and 30% after that, and 60% of the payments as income. `
                + `What it costs depends on the shape: with nothing coming back the capital is committed for life, and with capital returned the most that can be taken back falls each year to nothing. `
                + `Figures use an indicative rate; obtain a written quote from the provider before implementing, as the actual rate determines whether this strategy remains the strongest.`
        });
    }

    const productTypes = ['accountBasedPensions', 'superannuation', 'annuities', 'termDeposits'];
    const hasProductTypes = Object.keys(grouped).some((k) => productTypes.includes(k));
    if (hasProductTypes) {
        items.push({
            title: 'Product provider checks',
            lead: 'Before implementation, confirm product-level terms with each provider.',
            body: 'Break costs, exit fees, market-value adjustments, adviser service terms, and processing windows are not modelled in AgedCareWise and can change your net outcomes.'
        });
    }

    if (cgtTotal > 0) {
        items.push({
            title: 'Tax validation required',
            lead: `The current modelling estimates CGT of <strong>${formatCurrency(cgtTotal)}</strong> from asset sales used to fund RAD.`,
            body: 'This is an estimate only and should be validated with a registered tax adviser using actual cost-base records, ownership structure, and the final transaction date. Where no cost base was recorded for an asset, a cost base of 25% of its current value has been assumed (a conservative default). For gains realised on or after 1 July 2027, the legislated CGT reform applies (cost-base indexation in place of the 50% discount, and a 30% minimum tax that does not apply to Age Pension / income-support recipients).'
        });
    }

    if (cgtAssumptionDisclosure) {
        items.push({
            title: 'Incomplete records — CGT inputs assumed',
            lead: 'Some capital gains tax figures in this advice rest on assumptions, because the underlying records were not supplied.',
            body: cgtAssumptionDisclosure
        });
    }

    return items;
}

function buildSurplusDeficitText(row: SoaProjectionYear): string {
    const cashflow = toNumber(row.cashFlow);
    if (cashflow < 0) {
        const contributingSources = (row.drawdownBreakdown || [])
            .filter((d) => parseMoneyish(d?.value) > 0)
            .map((d) => sanitizeText(d?.label || ''))
            .filter(Boolean);
        if (contributingSources.length > 1) {
            return `Deficit funded from ${contributingSources.length} accounts`;
        }
        const source = contributingSources[0] || 'liquid assets';
        return `Deficit funded from ${source}`;
    }
    const preferred = (row.assets?.bankAccounts || []).find((a) => toNumber(a?.value || a?.balance) > 0);
    if (preferred) {
        return `Surplus directed to ${sanitizeText(preferred.description || preferred.name || 'Bank Account')}`;
    }
    return 'Surplus retained in portfolio';
}

/** Matches Deep Dive Overall Opportunity Cost: Year 1 investment income from snapshots (do nothing − selected). */
function estimateInvestmentEarningsLikeApp(selectedScenario: SoaScenario | null | undefined, doNothingScenario: SoaScenario | null | undefined): number {
    const selectedY1 = toNumber(selectedScenario?.snapshot?.investmentIncome);
    const doNothingY1 = toNumber(doNothingScenario?.snapshot?.investmentIncome);
    return doNothingY1 - selectedY1;
}

function superNomLabel(v: unknown): string {
    if (v === 'binding') return 'Binding nomination';
    if (v === 'non_binding') return 'Non-binding nomination';
    if (v === 'none') return 'None in place';
    return toDisplay(v);
}

function buildEstatePlanningBodyHtml(snapshot: SoaSnapshot): string {
    const f = snapshot.familyEstate || {};
    const clientFirst = sanitizeText(snapshot.clientName.split(/\s+/)[0] || 'Client');
    const partnerFirst = snapshot.partnerName ? sanitizeText(snapshot.partnerName.split(/\s+/)[0]) : '';

    const clientCol = clientFirst;
    const partnerCol = partnerFirst || '—';

    const missingWill = ['no', 'unsure'].includes(f.clientWill || '') || ['no', 'unsure'].includes(f.partnerWill || '');
    const missingEpoa = ['no', 'unsure'].includes(f.clientEpoa || '') || ['no', 'unsure'].includes(f.partnerEpoa || '');
    const showStatePicker = missingWill || missingEpoa;

    const picker = showStatePicker
        ? `<div class="state-picker card-inset">
<p class="small" style="margin-top:0"><strong>Estate planning legal pathways (by state/territory)</strong></p>
<label class="sel-label" for="estateStateSel">State or territory</label>
<select id="estateStateSel" class="sel-state">
<option value="">Select…</option>
<option value="NSW">New South Wales</option>
<option value="VIC">Victoria</option>
<option value="QLD">Queensland</option>
<option value="WA">Western Australia</option>
<option value="SA">South Australia</option>
<option value="TAS">Tasmania</option>
<option value="NT">Northern Territory</option>
<option value="ACT">Australian Capital Territory</option>
</select>
<div id="estateStateLinks" class="state-links small"></div>
</div>`
        : '';

    const implications = [];
    const actions = [];

    if (missingWill) {
        implications.push('One or more Wills are missing or uncertain. If not resolved, intestacy rules may determine asset distribution rather than your intended outcomes.');
        actions.push('Arrange a solicitor appointment to prepare or validate Wills for each relevant person.');
    }

    if (missingEpoa) {
        implications.push('Without an EPOA, family members may need tribunal/court intervention to make financial decisions if capacity is lost.');
        actions.push('Put EPOA documents in place while capacity exists and provide copies to trusted family members.');
    }

    const missingSuperNoms =
        !f.clientSuperNom || f.clientSuperNom === 'none' || (partnerFirst && (!f.partnerSuperNom || f.partnerSuperNom === 'none'));
    if (missingSuperNoms) {
        implications.push('Missing super death benefit nominations can delay benefit payment and may leave final beneficiary decisions to trustee discretion.');
        actions.push('Update super death benefit nominations (binding where appropriate) and diarise renewal/review dates.');
    }

    if (f.complexDynamics === true || f.unevenAssistance === true) {
        implications.push('Complex family dynamics and/or unequal financial support may increase the risk of estate disputes without clear documentation.');
        actions.push('Record a clear statement of wishes and have your estate documents reviewed for challenge risk.');
    }

    if (f.childrenCount != null) {
        implications.push(`Children/dependants noted in fact-find: ${sanitizeText(String(f.childrenCount))}.`);
    }

    if (implications.length === 0) {
        implications.push('No critical estate-planning gaps were detected from the current responses; ongoing review remains important.');
    }
    // What this plan does to the estate in practice (owner, 9 Oct 2026: the actions box printed
    // empty where nothing was missing). Facts, beside the actions, whatever is or is not missing.
    const worthConsidering = estateWorthConsidering({
        radPaid: toNumber(snapshot.radPaid),
        leavesVillage: !!snapshot.villageExit,
        hasSuper: (snapshot.situation?.assetsByType || []).some((r) => /super|account.based/i.test(String(r.type))),
        hasEpoa: f.clientEpoa === 'yes' && (!partnerFirst || f.partnerEpoa === 'yes'),
        planSells: snapshot.radFundingRows.length > 0,
        moneyWords: formatCurrency,
    });

    const tableHtml = `
<table>
<thead><tr><th>Estate directive</th><th>${clientCol}</th><th>${partnerCol}</th></tr></thead>
<tbody>
<tr><td><strong>Will</strong></td><td>${ynRecordLabel(f.clientWill)}</td><td>${partnerFirst ? ynRecordLabel(f.partnerWill) : '—'}</td></tr>
<tr><td><strong>Enduring Power of Attorney (EPOA)</strong></td><td>${ynRecordLabel(f.clientEpoa)}</td><td>${partnerFirst ? ynRecordLabel(f.partnerEpoa) : '—'}</td></tr>
<tr><td><strong>Advanced Care Directive (ACD)</strong></td><td>${ynRecordLabel(f.clientAcd)}</td><td>${partnerFirst ? ynRecordLabel(f.partnerAcd) : '—'}</td></tr>
<tr><td><strong>Super death benefit nomination</strong></td><td>${superNomLabel(f.clientSuperNom)}</td><td>${partnerFirst ? superNomLabel(f.partnerSuperNom) : '—'}</td></tr>
</tbody>
</table>`;

    const legalHtml =
        '<div class="callout callout--slate"><p class="callout-title">Legal note</p><p><strong>This Statement of Advice is not legal advice.</strong> It does not prepare Wills, powers of attorney, binding death benefit nominations, or trusts. A qualified solicitor should draft or amend those documents.</p></div>';

    const implicationsHtml = `<div class="callout callout--slate"><p class="callout-title">Implications from your responses</p><ul class="riskList">${implications.map((x) => `<li>${x}</li>`).join('')}</ul></div>`;
    const actionsHtml = actions.length
        ? `<div class="callout callout--amber"><p class="callout-title">Recommended actions</p><ul class="riskList">${actions.map((x) => `<li>${x}</li>`).join('')}</ul></div>`
        : '';
    const worthConsideringHtml = `<div class="callout callout--slate"><p class="callout-title">Worth considering as the plan is carried out</p><ul class="riskList">${worthConsidering.map((x) => `<li>${x}</li>`).join('')}</ul></div>`;

    return `${tableHtml}${implicationsHtml}${actionsHtml}${worthConsideringHtml}${picker}${legalHtml}`;
}

/** Back from the snapshot's escaped text to plain words, for the kit's pieces that escape for themselves. */
function plainText(value: unknown): string {
    return String(value ?? '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
}

function buildHtml(snapshot: SoaSnapshot): string {
    const period = describeProjectionPeriod(snapshot.projectionPeriodMode);
    const careTimingSentence = careAndSaleTimingSentence(snapshot);
    // The staleness safeguard's third and most important surface: this document is emailed, printed
    // and read weeks after the session that produced it, so the notice must travel INSIDE it. Same
    // single date as the build tripwire and the Assumptions page — refresh the tables, move
    // RATES_REVIEW_BY, and all three go quiet together.
    const ratesReview = ratesReviewStatus();
    const ratesReviewNoticeHtml = ratesReview.notice
        ? `<li><strong>Rates currency</strong> — ${sanitizeText(ratesReview.notice)}</li>`
        : '';

    const mountainA = stockPhoto('1559827260-dc66d52bef19', 1920, 1080);
    const mountainB = stockPhoto('1506905925346-21bda4d32df4', 1920, 1080);

    const primaryStrategyCards =
        Array.isArray(snapshot.optimalStrategyCardsPrimary) && snapshot.optimalStrategyCardsPrimary.length > 0
            ? snapshot.optimalStrategyCardsPrimary
            : snapshot.optimalStrategyCards || [];
    const conditionalStrategyCards = Array.isArray(snapshot.optimalStrategyCardsConditional)
        ? snapshot.optimalStrategyCardsConditional
        : [];

    const renderOptCardHtml = (card: {
        objectiveLabel?: string;
        selected?: boolean;
        cardKind?: string;
        name?: string;
        narrative?: string;
        legacy?: number;
        legacyMetricLabel?: string;
        cashflow?: number;
        pension?: number;
        fundingRows?: { source: string; amount: number }[];
        horizonWarning?: string | null;
    }) => {
        const legacyLabel = card.legacyMetricLabel || 'Legacy (modelled)';
        const statusPill = card.selected
            ? '<span class="pill-selected">Your selection</span>'
            : '<span class="pill-not-selected">Shown for comparison — not selected</span>';
        const kindClass =
            card.cardKind === 'partner_later' || card.cardKind === 'lower_rad' ? 'opt-card--conditional' : '';
        return `
<div class="opt-card ${card.selected ? 'opt-card--selected' : ''} ${kindClass}">
  <div class="opt-card-head">
    <div class="opt-tag">${card.objectiveLabel}</div>
    <div class="opt-card-status">${statusPill}</div>
  </div>
  <h3 class="opt-title">${card.name}</h3>
  <div class="opt-card-main">
    <p class="opt-narr">${card.narrative || 'Outcome driven by your fact-find and the funding mix modelled for this objective.'}</p>
    ${card.horizonWarning ? `<div class="callout callout--amber"><p class="callout-title">Worth knowing</p><p>${sanitizeText(card.horizonWarning)}</p></div>` : ''}
    <div class="opt-metrics">
      <div class="opt-metric-line"><span class="muted small">${legacyLabel}</span><strong>${formatCurrency(card.legacy)}</strong></div>
      <div class="opt-metric-line"><span class="muted small">Cashflow / year</span><strong>${formatCurrency(card.cashflow)}</strong></div>
      <div class="opt-metric-line"><span class="muted small">Age Pension / year</span><strong>${formatCurrency(card.pension)}</strong></div>
    </div>
  </div>
  <details class="opt-funding">
    <summary>How this strategy funds the RAD</summary>
    ${card.fundingRows && card.fundingRows.length > 0
        ? `<ul class="tip-ul">${card.fundingRows
            .map((row) => `<li><strong>${row.source}:</strong> ${formatCurrency(row.amount)}</li>`)
            .join('')}</ul>`
        : '<p class="small muted">No dedicated RAD drawdown was required in this strategy.</p>'}
  </details>
</div>`;
    };

    const primaryCardsHtml = primaryStrategyCards.map(renderOptCardHtml).join('');
    const conditionalCardsHtml = conditionalStrategyCards.map(renderOptCardHtml).join('');

    const alternativeStrategiesLead =
        snapshot.strategySource === 'manual_control'
            ? `You confirmed <strong>${snapshot.selectedStrategyName}</strong> as your preferred strategy. The cards below show the manual strategies saved in your modelling session for side-by-side comparison.`
            : conditionalStrategyCards.length > 0
              ? `You confirmed <strong>${snapshot.selectedStrategyName}</strong> as your preferred strategy. The first row compares baseline objectives (projected legacy, cashflow, and Age Pension). Where applicable, a second row documents optional comparison scenarios from your modelling session—each labelled so it is clear what was illustrated and whether it was your selection.`
              : `You confirmed <strong>${snapshot.selectedStrategyName}</strong> as your preferred strategy. The three cards below show the strongest alternatives by objective (legacy, cashflow, and Age Pension) so the trade-offs are clear and documented.`;

    const alternativeStrategiesGrids =
        conditionalStrategyCards.length > 0
            ? `<div class="opt-section">
<div class="opt-section-label">Baseline objective comparison</div>
<div class="opt-grid">${primaryCardsHtml}</div>
</div>
<div class="callout callout--slate opt-conditional-intro">
<p class="callout-title">Additional comparison scenarios</p>
<p class="small" style="margin:0">These optional cards mirror lenses available in AgedCareWise (for example a partner entering care later, or a lower-RAD option when baseline legacy outcomes are close). Your confirmed strategy is marked <strong>Your selection</strong> on the relevant card. Any other card in this section is labelled <strong>Shown for comparison — not selected</strong> so it is clear what was presented in the tool.</p>
</div>
<div class="opt-section">
<div class="opt-section-label">Additional scenarios (optional lenses)</div>
<div class="opt-grid opt-grid--2">${conditionalCardsHtml}</div>
</div>`
            : `<div class="opt-grid">${primaryCardsHtml}</div>`;

    const hasOutOfScopeGoals = (snapshot.outOfScopeGoals || []).length > 0;
    const outOfScopeSet = new Set(snapshot.outOfScopeGoals || []);
    const goalsBlock =
        (snapshot.statedGoals || []).length > 0
            ? `<ul class="goalsList">${snapshot.statedGoals
                  .map((g) => `<li>${g}${outOfScopeSet.has(g) ? ' <span class="muted small">(not covered by this SOA)</span>' : ''}</li>`)
                  .join('')}</ul>`
            : '<p class="lede muted">Where you did not tick structured goals in the fact-find, this advice still reflects your financial inputs and the strategy mix we modelled.</p>';
    const outOfScopeGoalScopeLine = hasOutOfScopeGoals
        ? `<li>Living arrangement goals recorded in your fact-find but not selected as the advice pathway in this document (${snapshot.outOfScopeGoals.map((g) => sanitizeText(g)).join(', ')}).</li>`
        : '';
    const moduleSpecificScopeLine = hasOutOfScopeGoals
        ? '<li>For goals marked as not covered in this SOA, re-run modelling in the relevant module and produce a module-specific SOA when available.</li>'
        : '';
    const mixedHouseholdScopeLine =
        hasOutOfScopeGoals && snapshot.isCoupleAdvice && (snapshot.outOfScopeGoals || []).includes('Access home care support')
            ? '<li>Where one partner may enter residential aged care and the other may remain at home with Support at Home services, complete pathway-specific modelling in the Support at Home module and issue a separate module-specific SOA when that pathway is available.</li>'
            : '';

    const pathCompareBlock = snapshot.pathComparison
        ? `<div class="callout callout--blue">
<p class="callout-title">Compared to a “do nothing” scenario (same room price, same expense profile)</p>
<p>This is a like-for-like comparison: both your selected strategy and the do-nothing baseline use the same room price and expense settings. The differences shown are from the funding choices, not from changing the underlying care assumptions.</p>
<p class="small muted">Figures below are model outputs only; they are not guarantees of future balances or entitlements.</p>
<ul>
<li><strong>Projected estate:</strong> ${formatDeltaCurrency(snapshot.pathComparison.legacyDelta)} vs “do nothing”</li>
<li><strong>Age Pension (annual):</strong> ${formatDeltaCurrency(snapshot.pathComparison.pensionDelta)} vs “do nothing”</li>
<li><strong>Cashflow (annual):</strong> ${formatDeltaCurrency(snapshot.pathComparison.cashflowDelta)} vs “do nothing”</li>
<li><strong>Aged care fees (annual, modelled):</strong> ${formatDeltaCurrency(snapshot.pathComparison.careFeeDeltaVsDoNothing)} vs “do nothing”</li>
</ul>
</div>`
        : '<p class="muted">A comparable “do nothing” baseline was not in this modelling run, so numeric comparisons to that benchmark are not shown.</p>';

    const deficitFundingCallout =
        snapshot.cashflowIsNegative && snapshot.deficitFundingNarrative
            ? `<div class="callout callout--amber"><p class="callout-title">How the cashflow shortfall is funded in the model</p><p>A cashflow deficit means the assets are not generating sufficient income to meet expenses. It only becomes problematic if liquid assets become insufficient to meet those costs.</p><p>${snapshot.deficitFundingNarrative}</p>${snapshot.fundingRunway ? `<p><strong>How long the money lasts:</strong> ${sanitizeText(snapshot.fundingRunway.headline)} — ${sanitizeText(snapshot.fundingRunway.basis.toLowerCase())}${snapshot.fundingRunway.flag ? ` ${sanitizeText(snapshot.fundingRunway.flag)}` : ''}</p>` : ''}</div>`
            : snapshot.cashflowIsNegative
              ? '<div class="callout callout--amber"><p class="callout-title">Cashflow shortfall</p><p class="muted">The selected strategy shows a projected cashflow shortfall; detailed drawdown lines were not attached to this export.</p></div>'
              : '';

    const projectionTableBody = buildProjectionTableBodyFromSnapshot(snapshot);
    const projectionHowToReadHtml = `<details class="soa-proj-howto"><summary>How to read this</summary><div class="soa-proj-howto-body"><p class="small muted">These projections are <strong>illustrative only</strong>. They can change with the timing of your strategy, investment returns, and future threshold or rule changes.</p><p class="small muted" style="margin-top:10px"><strong>Comparison Yr1</strong> shows full-year rates for the first projection year — a <strong>normalised year</strong>, the same basis as the Financial Impact panel — so strategies can be compared like-for-like without the timing of events getting in the way. In real life, your first ${period.noun} is often only partly on the new strategy${period.differsFromTaxYear ? '' : ', which is why we show the optional full financial year below: a separate estimate that combines the before- and after-strategy positions within the year (day-weighted; illustrative only)'}. Where a future care start date is set, rates before that date reflect the position at home, and the year care begins blends the two.</p></div></details>`;
    const projectionDeficitContext =
        snapshot.cashflowAnalysisSummary?.hasDeficitYears
            ? `<div class="callout callout--blue">
<p class="callout-title">Interpreting modelled deficits</p>
<p>Some years show a cashflow deficit. This is common in aged care modelling and does not automatically mean the strategy fails. The key test is whether those shortfalls can be funded from available assets over time. In this model, the <strong>Surplus/Deficit</strong> column shows the planned funding path for each year.</p>
</div>`
            : '';
    const estateBodyHtml = buildEstatePlanningBodyHtml(snapshot);
    const invDelta = snapshot.opportunityCost ? Number(snapshot.opportunityCost.investmentEarningsLost) || 0 : 0;
    const opportunityCostInvKpiClass =
        invDelta > 0 ? 'kpi kpi--warn' : invDelta < 0 ? 'kpi kpi--gain' : 'kpi';
    const opportunityCostPanel = snapshot.opportunityCost
        ? `<div class="grid4">
<div class="${opportunityCostInvKpiClass}"><div class="label">Investment income (Comparison Yr1 vs do nothing)</div><div class="value">${formatDeltaCurrency(snapshot.opportunityCost.investmentEarningsLost)}</div></div>
<div class="kpi"><div class="label">Age Pension impact / year</div><div class="value">${formatDeltaCurrency(snapshot.opportunityCost.agePensionImpact)}</div></div>
<div class="kpi"><div class="label">Care costs saved / year</div><div class="value">${formatDeltaCurrency(snapshot.opportunityCost.careCostsSaved)}</div></div>
<div class="kpi"><div class="label">Estate impact (modelled)</div><div class="value">${formatDeltaCurrency(snapshot.opportunityCost.estateImpact)}</div></div>
</div>
<p class="small muted" style="margin-top:10px">Investment income uses Comparison Yr1 totals from projections (same basis as the app’s Yearly Projections). Positive means lower investment income under the selected strategy than doing nothing; negative means higher.</p>`
        : '<p class="small muted">A comparable do-nothing run was not attached, so overall opportunity cost is not shown in this export.</p>';
    // The village's exit payment and a current room's refund come first (9 Oct 2026).
    const radFundingCards = [...snapshot.radFundingPrelude, ...snapshot.radFundingRows];
    const radFundingExecCards =
        radFundingCards.length > 0
            ? `<div class="rad-card-grid">${radFundingCards
                  .map(
                      (row) => `<div class="rad-card">
<div class="rad-type">${row.assetType}</div>
<div class="rad-source">${row.source}</div>
<div class="rad-amount">${formatCurrency(row.amount)}</div>
</div>`
                  )
                  .join('')}</div>`
            : '<p class="small muted">No dedicated asset liquidation was required for RAD funding in this strategy.</p>';

    const firstPensionLines = snapshot.enrichedProjections?.[0]?.pensionBreakdown || [];
    const thresholdRows = firstPensionLines
        .filter((x) => /limit|threshold|test/i.test(String(x?.label || '')))
        .map((x) => `<tr><td>${sanitizeText(String(x.label || 'Pension setting'))}</td><td>${sanitizeText(String(x.value ?? 'Not available'))}</td><td>Derived from the model's year-1 social security breakdown</td></tr>`)
        .join('');

    const cashflowKpiClass = snapshot.cashflowIsNegative ? 'kpi kpi--warn' : 'kpi';

    return `<!doctype html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Statement of Advice - ${snapshot.clientName}</title>
<style>
:root{--ink:#0f172a;--muted:#64748b;--line:#e2e8f0;--card:#fff;--accent:#2563eb;--accent2:#4f46e5;--warn-bg:#fff7ed;--warn-border:#fdba74;--warn-ink:#9a3412;--radius:16px;--shadow:0 14px 40px rgba(15,23,42,.08)}
body{margin:0;font-family:Inter,Segoe UI,system-ui,sans-serif;background:var(--ink);color:var(--ink);line-height:1.55}
.cover{min-height:100vh;display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden}
.cover::before{content:'';position:absolute;inset:0;background-image:url('${mountainA}');background-size:cover;background-position:center;opacity:.38;animation:moveA 28s ease-in-out infinite}
.cover::after{content:'';position:absolute;inset:0;background-image:url('${mountainB}');background-size:cover;background-position:center;opacity:.22;animation:moveB 34s ease-in-out infinite}
.coverShade{position:absolute;inset:0;background:radial-gradient(120% 80% at 30% 20%,rgba(37,99,235,.45),rgba(15,23,42,.88))}
.coverBox{position:relative;z-index:1;max-width:1040px;width:88%;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.35);border-radius:28px;padding:40px 36px;color:#fff;backdrop-filter:blur(8px);box-shadow:0 24px 80px rgba(0,0,0,.35)}
.coverKicker{font-size:12px;letter-spacing:.22em;text-transform:uppercase;opacity:.9}
.coverTitle{font-size:clamp(36px,6vw,58px);line-height:1.05;font-weight:900;margin:10px 0 8px}
.clientNames{font-size:clamp(22px,3.5vw,32px);font-weight:700}
.meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:18px}
.meta span{font-size:11px;padding:8px 12px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.4)}
.openBtn{margin-top:26px;padding:14px 22px;border-radius:14px;border:none;font-weight:800;cursor:pointer;background:#fff;color:var(--ink);font-size:15px;box-shadow:0 8px 24px rgba(0,0,0,.2);transition:transform .15s ease}
.openBtn:hover{transform:translateY(-2px)}
.book{background:linear-gradient(180deg,#eef2ff 0%,#f8fafc 18%);max-width:1200px;margin:0 auto;padding:28px 20px 48px;scroll-margin-top:0;min-height:100vh}
#soa-document{scroll-margin-top:0}
.topNav{position:sticky;top:0;background:rgba(255,255,255,.92);border:1px solid var(--line);border-radius:14px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center;z-index:40;backdrop-filter:blur(10px);box-shadow:var(--shadow);margin-bottom:18px}
.topNav .buttons{display:flex;gap:8px;flex-wrap:wrap}
.topNav button{padding:9px 14px;min-width:90px;border:1px solid #c7d2fe;background:linear-gradient(180deg,#fff,#f1f5ff);border-radius:10px;cursor:pointer;font-weight:700;font-size:13px;color:var(--ink)}
.topNav button:hover{border-color:var(--accent);color:var(--accent)}
.pager{font-size:12px;font-weight:700;color:var(--muted)}
.page{display:none;animation:fadeIn .35s ease}
.page.active{display:block}
@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
.card{background:var(--card);border:1px solid var(--line);border-radius:var(--radius);padding:26px 24px;margin-top:14px;box-shadow:var(--shadow);position:relative;overflow:visible}
.card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,#6366f1,#3b82f6);opacity:.9}
.h2{font-size:26px;font-weight:900;margin:0 0 12px;letter-spacing:-.02em}
.h3{font-size:17px;font-weight:800;margin:22px 0 10px;color:#1e293b}
.lede{font-size:16px;margin:10px 0 14px}
.muted{color:var(--muted)}.small{font-size:13px}.tiny{font-size:12px}
.goalsList{margin:8px 0 16px;padding-left:20px}
.goalsList li{margin:6px 0}
.grid4{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}
.kpi{background:linear-gradient(180deg,#f8fafc,#fff);border:1px solid var(--line);border-radius:12px;padding:14px 12px}
.kpi--warn{background:var(--warn-bg);border-color:var(--warn-border)}
.kpi--warn .value{color:var(--warn-ink)}
.kpi--gain{background:#ecfdf5;border-color:#a7f3d0}
.kpi--gain .value{color:#047857}
.label{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em;font-weight:700}
.value{font-size:24px;font-weight:900;margin-top:4px}
.callout{border-radius:14px;padding:16px 18px;margin:16px 0}
.callout--blue{background:#eff6ff;border:1px solid #bfdbfe}
.callout--amber{background:#fffbeb;border:1px solid #fcd34d}
.callout--slate{background:#f8fafc;border:1px solid #e2e8f0}
.callout-title{font-weight:800;margin:0 0 8px;font-size:15px}
.card-inset{background:#fff;border:1px dashed #cbd5e1;border-radius:12px;padding:14px 16px;margin:14px 0}
.state-picker .sel-label{display:block;font-size:12px;font-weight:700;margin-bottom:6px;color:#475569}
.sel-state{width:100%;max-width:320px;padding:10px 12px;border-radius:10px;border:1px solid var(--line);font-size:14px;background:#fff}
.state-links{margin-top:10px;line-height:1.6}
.state-links a{color:var(--accent);font-weight:700}
.estate-alert{background:#fef2f2;border-left:4px solid #f87171;padding:12px 14px;border-radius:0 10px 10px 0}
.tip-ul{margin:8px 0 2px 16px;padding:0;color:#334155;line-height:1.45;max-height:220px;overflow:auto}
.td-num{vertical-align:top;min-width:120px}
.proj-year{white-space:nowrap;font-weight:700;color:#1f2937}
.metric-cell{display:inline-flex;align-items:center;gap:6px}
.cc-tip-wrap{position:relative;display:inline-block}
.cc-tip-trigger{width:16px;height:16px;border-radius:999px;border:1px solid #cbd5e1;background:#fff;color:#64748b;font-size:10px;line-height:1;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0}
.cc-tip-trigger:hover{border-color:#94a3b8;color:#334155}
.cc-tip{position:absolute;left:50%;top:22px;transform:translateX(-50%);width:290px;max-width:min(290px,72vw);padding:10px 12px;border-radius:10px;border:1px solid #1f2a44;background:#111b2f;color:#f9fafb;box-shadow:0 12px 30px rgba(2,6,23,.45);font-size:12px;line-height:1.35;display:none;z-index:60;text-align:left}
.cc-tip.up{top:auto;bottom:22px}
.cc-tip strong{display:block;color:#7dd3fc;margin-bottom:8px;font-size:13px}
.tip-row{display:flex;justify-content:space-between;gap:12px;padding:2px 0}
.tip-lbl{color:#cbd5e1}
.tip-val{color:#ffffff;font-weight:700;white-space:nowrap}
.cc-tip-wrap:hover .cc-tip,.cc-tip-wrap:focus-within .cc-tip{display:block}
.age-pos{color:#16a34a}
.age-neg{color:#dc2626}
.rad-card-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;margin:10px 0 14px}
.rad-card{border:1px solid var(--line);border-radius:12px;background:#fff;padding:12px}
.rad-type{font-size:10px;letter-spacing:.08em;text-transform:uppercase;font-weight:800;color:#64748b}
.rad-source{font-size:13px;font-weight:700;color:#0f172a;margin-top:4px}
.rad-amount{font-size:20px;font-weight:900;color:#2563eb;margin-top:6px}
.imp-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px;margin-top:10px}
.imp-card{border:1px solid #dbeafe;background:#f8fbff;border-radius:12px;padding:14px}
.imp-title{font-size:14px;font-weight:800;color:#1e3a8a;margin:0 0 8px}
.imp-lead{font-size:13px;color:#0f172a;margin:0 0 6px}
.imp-body{font-size:13px;color:#334155;margin:0}
.yr-badge{display:inline-block;background:linear-gradient(135deg,#4f46e5,#2563eb);color:#fff;font-size:11px;font-weight:900;padding:4px 10px;border-radius:999px}
.table-proj{font-variant-numeric:tabular-nums}
.table-proj .td-num{white-space:nowrap}
.table-proj tbody tr.proj-tr-data.proj-zebra{background:#fafafa}
.soa-proj-howto{margin:0 0 14px;border:1px solid var(--line);border-left:3px solid var(--accent);background:#fff;border-radius:0 12px 12px 0;padding:0;overflow:hidden}
.soa-proj-howto summary{cursor:pointer;font-weight:700;color:var(--ink);padding:12px 14px}
.soa-proj-howto-body{padding:0 14px 14px}
.soa-fy-details{border:1px solid var(--line);border-left:3px solid var(--accent);background:#fff;border-radius:0 10px 10px 0;margin:0;padding:0}
.soa-fy-summary{cursor:pointer;font-weight:600;color:var(--ink);padding:10px 12px}
.soa-fy-est{font-weight:400;color:var(--muted)}
.soa-fy-inner-wrap{padding:0 12px 12px}
.soa-fy-lede{margin:0 0 10px}
.table-proj-fy{width:100%;font-size:13px;margin-top:6px;border-collapse:collapse}
.table-proj-fy td{border-top:1px solid var(--line);vertical-align:top}
.proj-tr-fy-wrap td{border-bottom:none;padding:8px 0 12px;background:transparent !important}
.proj-tr-fy-data td{background:#f8fafc !important;border-bottom:1px solid var(--line)}
.block{display:block}
.proj-cmp-label{display:block;font-size:14px}
.proj-tabs{display:flex;gap:18px;border-bottom:1px solid #e5e7eb;margin:8px 0 14px}
.proj-tab{appearance:none;border:none;background:none;padding:10px 0;color:#6b7280;font-weight:600;font-size:14px;cursor:pointer;border-bottom:2px solid transparent}
.proj-tab.active{color:#2563eb;border-bottom-color:#3b82f6}
.proj-panel{display:none}
.proj-panel.active{display:block}
.section-ribbon{height:4px;border-radius:99px;background:linear-gradient(90deg,#4f46e5,#2563eb,#06b6d4);margin-bottom:16px}
.scope-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:12px}
.scope-in{background:#ecfdf5;border:1px solid #6ee7b7;border-radius:12px;padding:14px}
.scope-out{background:#fef2f2;border:1px solid #fecaca;border-radius:12px;padding:14px}
.scope-in h4,.scope-out h4{margin:0 0 8px;font-size:13px;text-transform:uppercase;letter-spacing:.06em}
.scope-in ul,.scope-out ul{margin:0;padding-left:18px;font-size:14px}
.nav-dots{display:flex;gap:4px;align-items:center;margin-left:8px}
.nav-dot{width:6px;height:6px;border-radius:99px;background:#cbd5e1}
.nav-dot.on{background:var(--accent);box-shadow:0 0 0 2px rgba(37,99,235,.25)}
.opt-section{margin-top:6px}
.opt-section-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#64748b;margin:18px 0 10px}
.opt-conditional-intro{margin-top:8px !important}
.opt-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:14px;align-items:stretch}
.opt-grid--2{grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-top:10px;align-items:stretch}
.opt-grid>.opt-card{height:100%}
.opt-card{border:1px solid var(--line);border-radius:14px;padding:16px;background:#fafafa;transition:box-shadow .2s,border-color .2s,transform .2s;display:flex;flex-direction:column;min-height:100%;box-sizing:border-box}
.opt-card:hover{box-shadow:var(--shadow);transform:translateY(-2px)}
.opt-card--selected{border-color:#818cf8;background:linear-gradient(160deg,#eef2ff,#fff);box-shadow:0 12px 36px rgba(79,70,229,.15)}
.opt-card--conditional{border-left:4px solid #0d9488;background:linear-gradient(152deg,#f0fdfa 0%,#fafafa 55%)}
.opt-card-head{display:flex;flex-direction:column;align-items:flex-start;gap:8px;margin-bottom:8px;width:100%}
.opt-card-head .opt-tag{display:inline-block;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:var(--accent);background:#eff6ff;padding:4px 8px;border-radius:999px;max-width:100%}
.opt-card-status{display:block;width:100%}
.opt-card-status .pill-selected,.opt-card-status .pill-not-selected{display:inline-block;vertical-align:middle}
.pill-not-selected{font-size:10px;font-weight:800;background:#e2e8f0;color:#475569;padding:4px 10px;border-radius:999px;letter-spacing:.02em}
.opt-title{font-size:16px;margin:0 0 10px;font-weight:800;line-height:1.25}
.pill-selected{font-size:10px;font-weight:800;background:#22c55e;color:#fff;padding:4px 10px;border-radius:999px;letter-spacing:.02em}
.opt-card-main{flex:1 1 auto;display:flex;flex-direction:column;min-height:0}
.opt-narr{font-size:14px;color:#475569;margin:0 0 0;flex:1 1 auto;line-height:1.45}
.opt-metrics{margin-top:auto;padding-top:14px;border-top:1px solid #e2e8f0;flex-shrink:0;display:grid;grid-template-columns:1fr;gap:8px;font-size:14px}
.opt-metric-line{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.opt-metric-line strong{font-size:22px;line-height:1.2;white-space:nowrap}
.opt-funding{margin-top:10px;flex-shrink:0}
.opt-funding summary{cursor:pointer;font-weight:700;color:#334155}
.riskList{margin:0;padding-left:20px}
.riskList li{margin:10px 0}
table{width:100%;border-collapse:collapse;font-size:14px}
th,td{padding:11px 10px;border-bottom:1px solid var(--line);text-align:left}
th{background:#f1f5f9;font-weight:800;font-size:12px;text-transform:uppercase;letter-spacing:.04em;color:#475569}
details{margin:10px 0;border:1px solid var(--line);border-radius:12px;padding:12px;background:#fafafa}
details.inline-tip{margin:8px 0 0 0;padding:8px 10px}
.factSection{margin-top:8px}.factRow{display:grid;grid-template-columns:minmax(0,320px) 1fr;padding:10px 8px;border-bottom:1px solid #f1f5f9;gap:12px}.factLabel{font-weight:700;color:#334155;font-size:13px}
pre{white-space:pre-wrap;background:var(--ink);color:#e2e8f0;padding:14px;border-radius:10px;max-height:280px;overflow:auto;font-size:11px}
@keyframes moveA{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.09) translate(-14px,-12px)}}
@keyframes moveB{0%,100%{transform:scale(1) translate(0,0)}50%{transform:scale(1.06) translate(16px,14px)}}
@media (max-width:1020px){.grid4{grid-template-columns:repeat(2,minmax(0,1fr))}.opt-grid,.opt-grid--2{grid-template-columns:1fr}.scope-grid{grid-template-columns:1fr}}
@media (max-width:640px){.grid4{grid-template-columns:1fr}.factRow{grid-template-columns:1fr}}
@media print{
  @page{size:A4 portrait;margin:12mm}
  body{background:#fff !important;color:#000 !important}
  .cover{min-height:auto !important;page-break-after:always}
  .cover::before,.cover::after,.coverShade{display:none !important}
  .coverBox{background:#fff !important;color:#000 !important;border:1px solid #e5e7eb !important;box-shadow:none !important;backdrop-filter:none !important}
  .openBtn{display:none !important}
  .book{max-width:none !important;margin:0 !important;padding:0 !important;background:#fff !important}
  .topNav,.nav-dots,.pager,.buttons{display:none !important}
  .page,.page.active{display:block !important;animation:none !important;break-inside:avoid-page;page-break-inside:avoid;margin:0 0 10mm 0}
  .card{box-shadow:none !important;border:1px solid #d1d5db !important;break-inside:avoid-page;page-break-inside:avoid;background:#fff !important}
  .proj-tabs{display:none !important}
  .proj-panel{display:block !important}
  .cc-tip-wrap .cc-tip{display:none !important}
  details{border:1px solid #d1d5db !important;background:#fff !important}
  details[open] summary{margin-bottom:6px}
  table{font-size:11px !important}
  th,td{padding:6px 6px !important}
  a[href]:after{content:" (" attr(href) ")";font-size:10px;color:#6b7280}
}
${SOA_THEME_CSS}
</style></head><body>
<section class="cover"><div class="coverShade"></div><div class="coverBox">
<div class="coverKicker">Statement of advice</div>
<div class="coverTitle">Your aged care funding strategy</div>
<div class="clientNames">${snapshot.clientName}${snapshot.partnerName ? ` & ${snapshot.partnerName}` : ''}</div>
<div class="meta">
${snapshot.adviceAttributionLine
    ? `<span>${escapeHtml(snapshot.adviceAttributionLine)}</span>`
    : '<span>AgedCareWise Pty Ltd</span><span>AFSL 401234 (Demo)</span><span>ABN 12 345 678 901 (Demo)</span>'}
<span>Report ${snapshot.reportId}</span><span>${formatDateOnly(snapshot.generatedAt)}</span>
<span>Source: ${snapshot.strategySource === 'manual_control' ? 'MC (Manual Control)' : 'SE (Suggestion Engine)'}</span>
${snapshot.completerDisplay ? `<span>Completed by: ${snapshot.completerDisplay}${snapshot.completerRelationship ? ` (${snapshot.completerRelationship})` : ''}</span>` : ''}
</div>
<button type="button" class="openBtn" onclick="openBooklet()">Begin reading your advice</button>
<p class="muted" style="margin-top:14px;font-size:13px;opacity:.85">Opens your Statement of Advice below — use <strong>Next</strong> / <strong>Previous</strong> to move through each section.</p>
</div></section>
<div class="book" id="soa-document">
<div class="topNav">
  <div class="buttons">
    <button type="button" onclick="prevPage()">Previous</button>
    <button type="button" onclick="nextPage()">Next</button>
    <button type="button" data-goto="0">Contents</button>
  </div>
  <div class="pager" id="pager"></div>
</div>

<section class="page active"><div class="card">
<div class="h2">Executive summary</div>
${snapshot.executiveSummaryOpeningHtml || ''}
<p class="lede">${snapshot.careRecipientProfile}</p>
${snapshot.partnerContextHtml || ''}
${snapshot.wideExplorationClause || ''}
<p class="lede">${snapshot.strategySource === 'manual_control'
    ? `You selected <strong>${snapshot.selectedStrategyName}</strong> as the preferred manual strategy. This Statement of Advice follows that strategy through to projected outcomes.`
    : `We simulated <strong>${snapshot.totalScenarios}</strong> strategy combinations and highlighted the top three strategies by legacy, cashflow, and Age Pension. You selected <strong>${snapshot.selectedStrategyName}</strong>, and this Statement of Advice follows that strategy through to projected outcomes.`}</p>
<p>${snapshot.selectedStrategyNarrative}</p>
${snapshot.horizonSetAsideNote ? `<p class="lede">${snapshot.horizonSetAsideNote}</p>` : ''}
${snapshot.isCoupleAdvice && snapshot.illnessSeparatedAppliesInSoa ? `<div class="callout callout--blue">
<p class="callout-title">Age Pension Treatment</p>
<p>Before this advice, the baseline model shows you were <strong>${snapshot.baselinePensionStatus}</strong> at <strong>${formatCurrency(snapshot.baseline.pension)} p.a.</strong>.</p>
<p>Because <strong>${sanitizeText(snapshot.enteringCarePersonName)}</strong> is modelled as entering residential aged care, this strategy applies <strong>illness-separated couple</strong> Age Pension treatment in the scenario.</p>
<p>Under illness-separated treatment, Centrelink may apply <strong>different couple thresholds</strong> (including higher free areas/cut-offs than a standard-couple-at-home setting), so the Age Pension result can change even before RAD/DAP choices are considered.</p>
<p>For your selected strategy, the modelled Age Pension outcome is <strong>${formatCurrency(snapshot.annualPension)} p.a.</strong> (${snapshot.pensionDeltaFromBaseline >= 0 ? '+' : '-'}${formatCurrency(Math.abs(snapshot.pensionDeltaFromBaseline))} vs baseline), which is consistent with being <strong>${snapshot.selectedPensionStatus}</strong> in this model.</p>
<p class="small muted"><strong>Action required:</strong> Inform Services Australia when residential aged care commences and ask them to confirm the correct couple/illness-separated treatment on your record. Final entitlement is determined by Services Australia.</p>
</div>` : ''}

<div class="callout callout--slate">
<p class="callout-title">Accommodation price (anchor for the model)</p>
<p>The room price used for our financial modelling is <strong>${formatCurrency(snapshot.roomCost)}</strong> with your stated expenses when moving into aged care set at <strong>${formatCurrency(snapshot.adjustedLivingExpenses)} per year</strong>. This sets the do-nothing baseline, and all funding strategy options are then compared against that same baseline.</p>
</div>

<div class="h3">What you told us you are trying to achieve</div>
${goalsBlock}
<div class="h3">Snapshot of modelled outcomes</div>
<p>These are the headline results for your selected strategy. The projection section below shows what happens year by year.</p>

<div class="grid4">
<div class="kpi"><div class="label">Room price (accommodation)</div><div class="value">${formatCurrency(snapshot.roomCost)}</div></div>
<div class="kpi"><div class="label">Projected legacy</div><div class="value">${formatCurrency(snapshot.projectedLegacy)}</div></div>
<div class="kpi"><div class="label">Age Pension / year</div><div class="value">${formatCurrency(snapshot.annualPension)}</div></div>
<div class="${cashflowKpiClass}"><div class="label">Cashflow / year</div><div class="value">${formatCurrency(snapshot.annualCashflow)}</div></div>
</div>

${deficitFundingCallout}

<div class="h3">How the RAD is funded in this strategy</div>
<p class="small muted">${snapshot.radFundingPrelude.length ? 'The deposit is funded from the following, in this order: money the plan already has coming first, then what is sold at entry.' : 'At entry to care, the model funds the RAD from the following assets.'}</p>
${radFundingExecCards}

${pathCompareBlock}
<div class="h3">In this document</div>
<div id="soa-toc-slot"></div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Scope of advice</div>
<p>This section sets clear boundaries for you: what this advice covers, what it does not, and where you still need specialist input (for example legal documents or tax implementation).</p>
<div class="scope-grid">
<div class="scope-in">
<h4>In scope</h4>
<ul>
<li>High-level pathways for funding <strong>residential aged care</strong> (RAD, DAP, hybrid) and how they interact with modelled assets, income, and Age Pension outcomes.</li>
<li>Scenario comparison across combinations you enabled (e.g. home retained, rented, downsized) and RAD/DAP mixes, using the assumptions in the appendix.</li>
<li>Illustrative <strong>cashflow, pension, care-fee and net-worth projections</strong> — not forecasts of actual future balances.</li>
<li>Education on how means-tested care components are <strong>estimated</strong> in the tool (subject to formal assessment by Services Australia and the provider).</li>
<li>General <strong>estate planning guidance</strong> based on your stated estate position (for example Will/EPOA status and practical implications where documents are missing).</li>
<li><strong>How the years are shown:</strong> the numbers appear in two ways. The <strong>annual position</strong> (Comparison Year 1 in the strategy tables) is a normalised full year — what a complete year looks like once this plan is in place — so strategies can be compared without the effect of where we are in the financial year. The <strong>year-by-year projections</strong> (Projection Outlook) are the actual timeline: ${sanitizeText(projectionPeriodConventionSentence(snapshot.projectionPeriodMode))} ${sanitizeText(careTimingSentence)} Implementation timing can still vary in practice, which can create part-year differences in tax, pension and aged care results.</li>
</ul>
</div>
<div class="scope-out">
<h4>Out of scope</h4>
<ul>
${outOfScopeGoalScopeLine}
<li><strong>Specific financial product advice</strong> (e.g. recommending a particular super fund, insurance product, or security).</li>
<li>Tax, legal, or estate <strong>implementation</strong> advice — including CGT calculations, trust structuring, or drafting Wills and powers of attorney.</li>
<li>Choosing or endorsing a <strong>particular aged care provider</strong> or room; fee quotes must come from the provider.</li>
<li>Product-provider <strong>break fees, exit fees, and surrender terms</strong> are not modelled and must be confirmed directly with providers before implementation.</li>
<li>Medical, aged care placement, or aged care needs assessment decision-making.</li>
<li>Foreign assets, complex business structures, or strategies not captured in your fact-find. A defined benefit pension recorded in your profile is modelled as paid, assessed and taxed; the decision to take one, or to commute one, is not.</li>
${moduleSpecificScopeLine}
${mixedHouseholdScopeLine}
</ul>
</div>
</div>
<p class="small muted">If your circumstances fall outside these boundaries, update the fact-find or seek a qualified specialist before acting.</p>
</div></section>

${snapshot.situation ? `<section class="page"><div class="card">
<div class="h2">Your situation today</div>
<p class="lede">The position this advice starts from, as you entered it, before the move into care — and what would be left to the family if you stayed where you are.</p>
${renderSituationHtml(
    snapshot.situation,
    {
        // Every tile on this page is the household at home today; the estate tile is the same
        // household staying put. The in-care baseline is the bridge sentence (owner, 9 Oct 2026).
        name: snapshot.stayPutLegacy != null ? 'Staying where you are' : 'Doing nothing',
        pension: snapshot.baseline.pension,
        cashflow: snapshot.baseline.cashflow,
        estate: snapshot.stayPutLegacy ?? snapshot.doNothingLegacy ?? snapshot.projectedLegacy,
        netWorth: snapshot.baseline.netWorth,
    },
    snapshot.legacyHorizonYears ?? 5,
    snapshot.stayPutLegacy != null && snapshot.doNothingLegacy != null && snapshot.doNothingCashflow != null
        ? situationBridgeWords({ cashflow: snapshot.doNothingCashflow, estate: snapshot.doNothingLegacy, years: snapshot.legacyHorizonYears ?? 5, moneyWords: formatCurrency })
        : null,
)}
</div></section>

` : ''}<section class="page"><div class="card">
<div class="h2">Accommodation &amp; daily care costs</div>

<div class="callout callout--slate">
<p class="callout-title">Your accommodation numbers (modelled)</p>
<p><strong>Room price:</strong> ${formatCurrency(snapshot.roomCost)} &nbsp;|&nbsp; <strong>RAD paid:</strong> ${formatCurrency(snapshot.radPaid)} &nbsp;|&nbsp; <strong>Estimated DAP (annual on unpaid balance):</strong> ${formatCurrency(snapshot.estimatedDapAnnual)}</p>
${snapshot.facilityMove ? `<p><strong>A change of home:</strong> ${snapshot.facilityMove}</p>` : ''}
${snapshot.villageExit ? `<p><strong>Leaving the village:</strong> ${snapshot.villageExit}</p>` : ''}
${snapshot.secondRoom ? `<p><strong>${snapshot.secondRoom.name}'s room:</strong> ${formatCurrency(snapshot.secondRoom.roomPrice)} &nbsp;|&nbsp; <strong>RAD paid:</strong> ${formatCurrency(snapshot.secondRoom.radPaid)}${snapshot.secondRoom.dailyTotal != null ? ` &nbsp;|&nbsp; <strong>Daily fees:</strong> ${formatCurrency(snapshot.secondRoom.dailyTotal)} a day` : ''}.${snapshot.secondRoom.phaseLine ? ` ${snapshot.secondRoom.phaseLine}` : ''} Both of you are in care, so Services Australia assesses you as a couple living apart — each paid the Age Pension at the single rate, and each room's fees worked out on half the household's income and assets.</p>` : ''}
</div>

<p><strong>RAD (Refundable Accommodation Deposit)</strong> is the lump sum option for the room. It is generally refunded when care ends, but your resident agreement can allow specific deductions. A common framework is retention up to <strong>2% p.a.</strong> of the RAD, capped over <strong>5 years</strong>, so the provider agreement should always be checked before signing.</p>
<p><strong>Drawing fees from the deposit.</strong> ${depositArrangementSentence(snapshot)}</p>
${feeArrangementsParagraph(snapshot)}
<p><strong>DAP (Daily Accommodation Payment)</strong> applies to any unpaid room balance. Think of this as <strong>rent on that unpaid amount</strong>: it is ongoing and <strong>not refundable</strong>. The amount is driven by the MPIR in force when the room is agreed.</p>
<p><strong>Hybrid</strong> simply means paying part of the room price as RAD and leaving part as DAP — often used to balance liquidity with lower daily accommodation charges.</p>

<div class="h3">Means-tested daily fees</div>
${snapshot.fees.feeArrangements === '2014'
    ? `<p>On top of accommodation, residents pay a <strong>basic daily fee</strong> (capped, linked to pension benchmarks) and, on the <strong>1 July 2014 fee arrangements</strong> that apply here, one means-tested fee:</p>
<ul>
<li><strong>Means-tested care fee</strong> — Services Australia adds an income-tested amount (50% of assessable income above the income free area) and an asset-tested amount (17.5%, 1% and 2% of successive slices of assessable assets, with the former home counted up to the home exemption cap), takes off the maximum accommodation supplement, and charges the result per day up to a daily maximum. It stops for the rest of a year of care once the annual cap is reached, and for good once the lifetime cap is reached; income-tested care fees already paid under a Home Care Package and Support at Home contributions count towards the lifetime cap.</li>
<li>The <strong>hotelling supplement contribution</strong> and <strong>non-clinical care contribution</strong> that residents on the 1 November 2025 arrangements pay <strong>do not apply</strong> on these arrangements.</li>
</ul>`
    : `<p>On top of accommodation, residents may pay a <strong>basic daily fee</strong> (capped, linked to pension benchmarks) and <strong>means-tested</strong> care contributions based on income and assets. In AgedCareWise’s calculation lines you will see:</p>
<ul>
<li><strong>Non-clinical care contribution (NCCC)</strong> — the income- and asset-tested contribution toward non-clinical care services, capped daily and by a lifetime amount and a four-year limit.</li>
<li><strong>Hotelling supplement contribution (HSC)</strong> — the contribution toward everyday living costs (meals, cleaning, laundry, utilities) for residents whose means-tested amount exceeds the accommodation supplement, capped daily.</li>
</ul>`}
<p class="muted small">Final dollar outcomes are determined by Services Australia and your provider after formal assessment; the table below is an estimate from the inputs you supplied.</p>

<table><thead><tr><th>Component</th><th>Estimated daily (model)</th></tr></thead><tbody>
<tr><td>Basic daily fee</td><td>${formatCurrency(snapshot.fees.basicDailyFee)}</td></tr>
${snapshot.fees.feeArrangements === '2014'
    ? `<tr><td>Means-tested care fee (1 July 2014 arrangements — no worse off)</td><td>${formatCurrency(snapshot.fees.dailyMTCF)}</td></tr>`
    : `<tr><td>Non-clinical care contribution (NCCC)</td><td>${formatCurrency(snapshot.fees.dailyNCCC)}</td></tr>
<tr><td>Hotelling supplement contribution (HSC)</td><td>${formatCurrency(snapshot.fees.dailyHSC)}</td></tr>`}
<tr><td>Extra service fee</td><td>${formatCurrency(snapshot.fees.dailyExtraServiceFee)}</td></tr>
</tbody></table>

<div class="h3">Assets used to fund RAD (if any)</div>
<table><thead><tr><th>Asset type</th><th>Asset</th><th>Amount</th></tr></thead><tbody>
${radFundingCards.length > 0 ? radFundingCards.map((row) => `<tr><td>${row.assetType}</td><td>${row.source}</td><td>${formatCurrency(row.amount)}</td></tr>`).join('') : '<tr><td colspan="3">No explicit drawdowns were required for RAD in this strategy.</td></tr>'}
</tbody></table>
</div></section>

<section class="page"><div class="card">
<div class="h2">Age Pension Treatment</div>
${snapshot.isCoupleAdvice
    ? `<p class="lede">This section explains how Age Pension couple thresholds were applied in this modelling and why your result may differ from a standard-couple snapshot.</p>
<div class="callout callout--slate">
<p><strong>Before advice (baseline):</strong> You were modelled as <strong>${snapshot.baselinePensionStatus}</strong> with Age Pension of <strong>${formatCurrency(snapshot.baseline.pension)} p.a.</strong> using <strong>${snapshot.baselineUsesIllnessSeparated ? 'illness-separated couple' : 'standard couple'}</strong> thresholds.</p>
<p><strong>Scenario assumption:</strong> With <strong>${sanitizeText(snapshot.enteringCarePersonName)}</strong> entering residential aged care, the selected strategy applies <strong>${snapshot.modelUsesIllnessSeparated ? 'illness-separated couple' : 'standard couple'}</strong> thresholds.</p>
<p><strong>What changes in rules:</strong> Illness-separated treatment can use a higher couple pension maximum and different income/asset free areas and cut-offs than standard-couple-at-home settings. This can change pension eligibility and payment rate.</p>
<p><strong>Your home:</strong> ${snapshot.homeRuleParagraph}</p>
<p><strong>Result in your recommendation:</strong> Modelled Age Pension is <strong>${formatCurrency(snapshot.annualPension)} p.a.</strong>, which is <strong>${snapshot.pensionDeltaFromBaseline >= 0 ? '+' : '-'}${formatCurrency(Math.abs(snapshot.pensionDeltaFromBaseline))}</strong> compared with baseline.</p>
<p><strong>What you need to do:</strong> Notify Services Australia when care starts and request confirmation that the correct illness-separated/couple treatment is applied to your case.</p>
<p class="small muted"><strong>Validation required:</strong> Final classification and entitlement are determined by Services Australia based on your actual circumstances and assessment timing.</p>
</div>
${pensionBreakdownDetailsHtml(snapshot)}`
    : `<p class="lede">This section explains how the Age Pension was assessed in this modelling, and how entering residential care changes the assessment for a single person.</p>
<div class="callout callout--slate">
<p><strong>Before advice (baseline):</strong> You were modelled as <strong>${snapshot.baselinePensionStatus}</strong> with Age Pension of <strong>${formatCurrency(snapshot.baseline.pension)} p.a.</strong> using <strong>single</strong> thresholds.</p>
<p><strong>Scenario assumption:</strong> With <strong>${sanitizeText(snapshot.enteringCarePersonName)}</strong> entering residential aged care, the selected strategy continues to apply <strong>single</strong> thresholds.</p>
<p><strong>What changes in rules:</strong> ${snapshot.homeRuleParagraph}</p>
<p><strong>Result in your recommendation:</strong> Modelled Age Pension is <strong>${formatCurrency(snapshot.annualPension)} p.a.</strong>, which is <strong>${snapshot.pensionDeltaFromBaseline >= 0 ? '+' : '-'}${formatCurrency(Math.abs(snapshot.pensionDeltaFromBaseline))}</strong> compared with baseline.</p>
<p><strong>What you need to do:</strong> Notify Services Australia when care starts, and again when the deposit is paid or the home is sold or rented, so the assessment is updated on the right dates.</p>
<p class="small muted"><strong>Validation required:</strong> Final entitlement is determined by Services Australia based on your actual circumstances and assessment timing.</p>
</div>
${pensionBreakdownDetailsHtml(snapshot)}`}
</div></section>

<section class="page"><div class="card">
<div class="h2">Implications of this advice</div>
<p class="lede">Because your selected strategy is funded through specific assets, this section highlights the key implementation implications before you action any transactions.</p>
<div class="imp-grid">
${(snapshot.adviceImplications || []).map((item) => `<div class="imp-card">
<p class="imp-title">${item.title}</p>
<p class="imp-lead">${item.lead}</p>
<p class="imp-body">${item.body}</p>
${item.cgtLine ? `<p class="imp-body" style="margin-top:6px">${item.cgtLine} Confirm final tax with your registered tax adviser before acting.</p>` : ''}
</div>`).join('')}
</div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Alternative strategies considered</div>
<p class="lede">${snapshot.scenarioExplainer}</p>
<p>${alternativeStrategiesLead}</p>
${alternativeStrategiesGrids}
${snapshot.horizonSetAsideHtml || ''}
</div></section>

<section class="page"><div class="card">
<div class="h2">Projection outlook</div>
<p class="muted small">This mirrors the projection view from AgedCareWise for <strong>${snapshot.selectedStrategyName}</strong>.</p>
<div class="h3" style="margin-top:4px">${snapshot.selectedStrategyName}</div>
${projectionDeficitContext}
<div class="proj-tabs">
  <button type="button" class="proj-tab active" data-tab="yearlyProjections">Yearly Projections</button>
  <button type="button" class="proj-tab" data-tab="opportunityCost">Overall Opportunity Cost</button>
</div>
<div class="proj-panel active" data-panel="yearlyProjections">
  ${renderProjectionCharts({
      idPrefix: 'ac-proj',
      planName: plainText(snapshot.selectedStrategyName),
      plan: (snapshot.enrichedProjections || []).map((r) => ({ label: plainText(String(r.fyLabel || `Year ${r.year}`)), pension: Number(r.pension) || 0, cashflow: Number(r.cashFlow) || 0, netWorth: Number(r.netWorth) || 0 })),
      baselineName: 'Doing nothing',
      baseline: (snapshot.baselineProjections || []).map((r) => ({ ...r, label: plainText(r.label) })),
  })}
  <div class="h3" style="margin-top:18px">5-Year Projections</div>
  ${projectionHowToReadHtml}
  <table class="table-proj"><thead><tr>
  <th>Year</th>
  <th>Net Worth (Opening)</th>
  <th>Age Pension</th>
  <th>Aged Care Costs</th>
  <th>Net Cashflow</th>
  <th>Net Worth (Closing)</th>
  <th>Surplus/Deficit</th>
  </tr></thead><tbody>
  ${projectionTableBody}
  </tbody></table>
</div>
<div class="proj-panel" data-panel="opportunityCost">
  <div class="h3" style="margin-top:4px">Opportunity Cost Analysis</div>
  ${opportunityCostPanel}
</div>
</div></section>

<section class="page"><div class="card">
<div class="h2">Estate planning</div>
${estateBodyHtml}
</div></section>

<section class="page"><div class="card">
<div class="h2">Fees &amp; disclosures</div>
<p><strong>Advice fee:</strong> Initial fee <strong>$450 plus GST</strong>. <strong>No ongoing advice fee</strong> and <strong>no separate implementation fee</strong> is charged for this digital pathway.</p>
<p><strong>Other costs:</strong> Aged care providers charge accommodation and care amounts separately; superannuation and investment products carry fees disclosed in their PDS or platform schedules.</p>
<p><strong>Conflicts of interest:</strong> AgedCareWise does <strong>not</strong> provide specific financial <em>product</em> advice. Recommendations are <strong>strategic</strong> and illustrated through the engine’s assumptions. Where the model shows drawdowns from particular assets, that ordering reflects <strong>liquidity, growth and tax assumptions you supplied</strong> — not a commission or conflict tied to a product issuer. On that basis, <strong>no conflicts are disclosed</strong> in relation to this advice.</p>
<p><strong>Complaints:</strong> Contact AgedCareWise using the details on your account / correspondence. If a complaint cannot be resolved, you may refer the matter to the Australian Financial Complaints Authority (AFCA) where eligible.</p>
<p class="muted small">${snapshot.adviceAttributionLine
    ? `${escapeHtml(snapshot.adviceAttributionLine)}${snapshot.adviceAttributionIncomplete
        ? ' <strong>Your firm profile is incomplete — finish it in Settings before issuing this to a client.</strong>'
        : ''}`
    : 'AgedCareWise Pty Ltd (Demo AFSL 401234) — ensure this block matches your licensee’s approved wording before client issue.'}</p>
</div></section>

${snapshot.strategyRisks?.sections?.length ? `<section class="page"><div class="card">
<div class="h2">What could go wrong with this strategy</div>
<p class="lede">Every plan has trade-offs. These are the ones that matter for the strategy recommended here, as they were shown to you when you chose it.</p>
${snapshot.strategyRisks.forYou.length ? `<div class="h3">For you specifically</div><ul class="riskList">${snapshot.strategyRisks.forYou.map((p) => `<li><strong>${escapeHtml(p.lead)}</strong> ${escapeHtml(p.body)}</li>`).join('')}</ul>` : ''}
${snapshot.strategyRisks.sections.map((s) => `<div class="h3">${escapeHtml(s.title)}</div><ul class="riskList">${s.points.map((p) => `<li><strong>${escapeHtml(p.lead)}</strong> ${escapeHtml(p.body)}</li>`).join('')}</ul>`).join('')}
</div></section>` : ''}

<section class="page"><div class="card">
<div class="h2">Risks &amp; limitations</div>
<ul class="riskList">
${ratesReviewNoticeHtml}<li><strong>Legislation &amp; policy</strong> — aged care, superannuation, and social security rules can change, which may shift outcomes.</li>
<li><strong>Modelling vs reality</strong> — figures depend on assumptions (returns, fees, timing of care, home decisions). Real-world outcomes will vary.</li>
${snapshot.assumptionsChanged ? `<li><strong>Assumptions you changed</strong> — we note that ${snapshot.assumptionsChanged.clientName} has changed ${snapshot.assumptionsChanged.rows.length === 1 ? 'one of' : ''} our default growth and income rate assumptions (see <em>Assumptions you changed</em> in the appendix). This Statement of Advice is based on mathematical calculations that factor in asset income, growth and capital gains tax assumptions. Changes to those assumptions can have a material effect on the projections and on the strategies the system recommends; the appendix shows the same plan at AgedCareWise's default rates for comparison.</li>` : ''}
<li><strong>Provider-specific terms</strong> — accommodation agreements, extra services, RAD refund timing, and any permissible retention amounts depend on your chosen provider’s contract.</li>
<li><strong>Tax &amp; legal</strong> — CGT and estate outcomes should be confirmed with a registered tax agent or solicitor where you implement asset sales or structural changes. Where a new home is bought, stamp duty is estimated from that state's published scale with no concessions applied, or not at all where no state was chosen; confirm the figure with your conveyancer.</li>
${snapshot.structureAppendix ? '<li><strong>Trusts &amp; companies</strong> — where structures are modelled, apportionment and entity-asset summaries are in the appendix. Care Companion does not advise on deed or constitution permissibility; entity funding in automatic suggestions follows your Strategy Analysis Setup choice (personal only vs include entity assets).</li>' : ''}
<li><strong>Timing alignment</strong> — actual outcomes can differ where sale dates, provider start dates, Centrelink reassessment timing, or tax-year boundaries differ from the model’s implementation timing.</li>
<li><strong>Information gaps</strong> — incomplete fact-find data may skew results; update inputs and re-run strategies when material facts change.</li>
</ul>
</div></section>

<section class="page"><div class="card">
<div class="h2">Next steps</div>
<ul class="riskList">
<li>Work through the implementation checklist inside AgedCareWise (RAD payment timing, asset drawdowns, Services Australia updates) using this Statement of Advice as the reference.</li>
${snapshot.pensionNextStep ? `<li>${snapshot.pensionNextStep}</li>` : ''}
<li>Obtain, read, and only then sign the aged care provider’s <strong>resident agreement</strong> — confirm retention rules, extra services, and refund conditions in writing, and consider having your own solicitor review the agreement before you sign.</li>
<li><strong>Re-run modelling</strong> whenever there is a significant change in your circumstances so pension, DAP and liquidity stay current.</li>
<li>Where you sell assets or trigger CGT, confirm final tax outcomes with a <strong>registered tax agent</strong>.</li>
<li>Update Wills, EPOA, and super nominations with a <strong>solicitor</strong> if the fact-find highlighted gaps.</li>
</ul>
</div></section>

<section class="page"><div class="card">
<div class="h2">Appendix — fact-find &amp; assumptions</div>
<p><strong>Client age:</strong> ${snapshot.clientAge}${snapshot.partnerName ? ` | <strong>Partner age:</strong> ${snapshot.partnerAge}` : ''}</p>
<p><strong>Living expenses (annual):</strong> ${formatCurrency(snapshot.annualLivingExpenses)} | <strong>Adjusted in model:</strong> ${formatCurrency(snapshot.adjustedLivingExpenses)}</p>
<div class="h3">Asset assumptions</div>
<table><thead><tr><th>Asset type</th><th>Description</th><th>Value</th><th>Income %</th><th>Growth %</th><th>Client's own figures</th></tr></thead><tbody>
${snapshot.assetRows.map((row) => `<tr><td>${row.assetType}</td><td>${row.description}${row.basis ? `<div class="tiny muted">${row.basis}</div>` : ''}</td><td>${formatCurrency(row.value)}</td><td>${row.incomeRate.toFixed(2)}%</td><td>${row.growthRate.toFixed(2)}%</td><td>${row.overridden ? 'Yes' : 'No'}</td></tr>`).join('')}
</tbody></table>
<p class="tiny muted">Income % is what the model applied. For an investment property it is the yield the recorded rent represents — the projections use the rent and expense dollars recorded for the property, not an assumed yield.</p>
${buildAssumptionsChangedHtml(snapshot)}
${snapshot.structureAppendixHtml || ''}
<div class="h3">Structured fact-find responses</div>
${snapshot.factFindSections.map((section) => `
<details class="factSection"><summary><strong>${section.title}</strong></summary>
  <div>
    ${section.rows.map((row) => `<div class="factRow"><div class="factLabel">${row.label}</div><div>${toDisplay(row.value)}</div></div>`).join('')}
  </div>
</details>`).join('')}
<details class="factSection"><summary><strong>Rates &amp; thresholds used</strong></summary>
<table><thead><tr><th>Rate / threshold</th><th>Value used</th><th>Notes</th></tr></thead><tbody>
<tr><td>MPIR (for DAP estimate)</td><td>${(AGED_CARE_SCHEDULE.MPIR * 100).toFixed(2)}%</td><td>Used to estimate DAP on unpaid room balance (${AGED_CARE_SCHEDULE.mpiQuarterLabel || 'see Department of Health quarterly publication'})</td></tr>
<tr><td>Advice fee (initial)</td><td>$450 + GST</td><td>No ongoing or implementation fee in this pathway</td></tr>
${thresholdRows || '<tr><td>Age pension / social security thresholds</td><td>See projection tooltips</td><td>Threshold lines were not attached in this export payload</td></tr>'}
<tr><td>Aged care means-tested values</td><td>Derived by AgedCareWise engine</td><td>Provider and Services Australia outcomes may differ after formal assessment</td></tr>
</tbody></table>
</details>
<div class="h3">Tax, super, Age Pension, aged care and market assumptions</div>
<p class="small muted">Every rate and threshold the model used, as the Assumptions page showed them when this document was generated. Open a group to read it.</p>
${renderAssumptionsAppendixHtml(snapshot.assumptionSections || buildAssumptionSections(), /Aged Care|Social Security|Tax/i)}
</div></section>

</div>
<script src="${soaBookletScriptUrl}"></script>
</body></html>`;
}

/**
 * The finished document as HTML, without opening or downloading anything — for the licensee
 * oversight view, which shows a stored Statement of Advice to a reviewer read-only.
 */
export function buildAgedCareDigitalSOAHtml(snapshot: SoaSnapshot): string {
    return buildHtml(snapshot);
}

export function openAgedCareDigitalSOAAttachment(snapshot: SoaSnapshot | null | undefined): void {
    if (!snapshot) return;
    const html = buildHtml(snapshot);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const safeName = snapshot.clientName.replace(/[^a-zA-Z0-9]/g, '_');

    const tab = window.open('', '_blank');
    if (tab) {
        tab.document.open();
        tab.document.write(html);
        tab.document.close();
    } else {
        const openLink = document.createElement('a');
        openLink.href = url;
        openLink.target = '_blank';
        openLink.rel = 'noopener noreferrer';
        document.body.appendChild(openLink);
        openLink.click();
        document.body.removeChild(openLink);
    }

    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = `Digital_SOA_${safeName}_${snapshot.generatedAt.slice(0, 10)}.html`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
}
